/**
 * Real CDC 2000 growth-chart LMS reference data, 2 through 20 years — `BACKLOG.md` M8, replacing
 * the plausible-shaped placeholder values `growthCharts.ts`'s `REFERENCE_TABLE` previously carried
 * for this age range (see that module's own header on the WHO-side placeholder, which this file
 * does not touch).
 *
 * **Source**: `growthfile_cdc_ext.csv`, bundled by `growthcleanr`
 * (https://github.com/carriedaymont/growthcleanr, Carrie Daymont's CDC-endorsed R package used by
 * CHOP/PEDSnet, MIT-licensed) at
 * https://raw.githubusercontent.com/carriedaymont/growthcleanr/main/inst/extdata/growthfile_cdc_ext.csv.gz
 * — itself a pre-flattened, single-row-per-age-day-per-sex re-derivation of the CDC's own official
 * SAS growth-chart macro reference table (`CDCref_d.csv`, same repository/directory), used here
 * because it avoids re-implementing that macro's own paired-endpoint interval/interpolation layout.
 * Fetched and independently verified 2026-08-24 (not just located): real gzip content (SHA-256
 * b805889fada64d41abe90e31eb7ade02e6ff48a4a395b753272eddc20bb3ac91 for the decompressed CSV),
 * `sex` confirmed empirically as `0 = male, 1 = female` (not documented in the file's own header —
 * derived by comparing median weight/height at 18 years, where the two sexes diverge unambiguously:
 * `sex=0` gives 67.2kg/176.2cm at 18y, matching published CDC male values; `sex=1` gives
 * 56.2kg/163.1cm, matching published CDC female values), and spot-checked medians against known CDC
 * 2000 published figures (e.g. male weight-for-age median ≈12.67kg at 24 months, matching the
 * commonly-cited ≈12.6–12.7kg) for plausibility and monotonicity across adjacent ages.
 *
 * **Sampling**: monthly (`growthCharts.ts`'s own `DAYS_PER_MONTH = 30.4375` convention, rounded the
 * same way, `Math.round(months * 30.4375)`), from month 24 (`ageDays: 731`) through month 240 / 20
 * years (`ageDays: 7305`, matching `Math.round(20 * 365.25)`). `whoGrowthReferenceData.ts`'s own
 * real data ends at `ageDays: 730` — one day short of this file's first row — so the two halves
 * hand off with no gap or overlap; neither module needs a standalone age-conversion helper any more
 * now that both real monthly tables carry every age point directly (`growthCharts.ts`'s own
 * `whoAgeDays`/`cdcAgeDays` helpers, used only to generate the old placeholder rows, are gone). The
 * source file itself is daily-resolution, smoothed from the CDC's official
 * monthly-interval publication for `growthcleanr`'s own outlier-detection use — sampling it back at
 * monthly resolution reconstructs the true official granularity rather than adding false precision.
 * Real per-day values are available in `growthfile_cdc_ext.csv` if a future pass wants finer
 * resolution; monthly was chosen as a large, defensible improvement over the ~2–3-year gaps the
 * previous placeholder table used, without embedding thousands of near-identical rows.
 *
 * **Head circumference**: CDC does not publish a head-circumference-for-age chart past 36 months
 * (head growth is essentially complete by then), so `CDC_HEAD_CIRCUMFERENCE_CM_REFERENCE` only spans
 * 24–36 months — genuinely narrower than the weight/height tables, not a truncation. Its real data
 * ends at `ageDays: 1095` exactly (one day short of the month-36 grid point), so that exact day is
 * used as the final row instead of forcing it onto the monthly grid.
 *
 * **BMI-for-age, added 2026-08-24** (`BACKLOG.md` M8's BMI-for-age follow-up): `growthfile_cdc_ext.
 * csv`'s `cdc_bmi_l/m/s` columns, confirmed empirically (not assumed) to be populated from
 * `ageDays: 731` on and unpopulated before it — the same re-fetch used for this file's own SHA-256
 * check above (`b805889f...`, unchanged, confirming this is the identical source file) was used to
 * extract these three columns at the same monthly resolution and rounding convention already used
 * for weight/height/head-circumference (`Math.round(months * 30.4375)`, months 24 through 240).
 * `CDC_BMI_REFERENCE` below is server-computed input only — BMI itself is never entered directly, see
 * `growthCharts.ts`'s `computeBmi` and `server/src/pediatrics/growthService.ts`'s `createGrowth
 * Measurement`. Medians spot-checked for plausibility against commonly-cited CDC 2000 BMI-for-age
 * figures: ~16.5-16.6 at 24 months, ~16.6-16.9 at 10 years, ~21-23 at 20 years (both sexes), all
 * matching this table's own `m` values at those exact grid points.
 */

interface CdcReferenceRow {
  readonly ageDays: number;
  readonly l: number;
  readonly m: number;
  readonly s: number;
}

/** Weight-for-age, 24 months through 20 years, monthly LMS values. */
export const CDC_WEIGHT_KG_REFERENCE: Readonly<
  Record<'male' | 'female', readonly CdcReferenceRow[]>
> = {
  male: [
    { ageDays: 731, l: -0.2064924, m: 12.67309, s: 0.1081271 },
    { ageDays: 761, l: -0.2277727, m: 12.81188, s: 0.1082159 },
    { ageDays: 791, l: -0.2523421, m: 12.94824, s: 0.1083414 },
    { ageDays: 822, l: -0.2808763, m: 13.08742, s: 0.1085094 },
    { ageDays: 852, l: -0.3111889, m: 13.22113, s: 0.1087088 },
    { ageDays: 883, l: -0.3448877, m: 13.35892, s: 0.1089526 },
    { ageDays: 913, l: -0.3793669, m: 13.4924, s: 0.1092248 },
    { ageDays: 944, l: -0.4164558, m: 13.63097, s: 0.1095429 },
    { ageDays: 974, l: -0.4532935, m: 13.76605, s: 0.109886 },
    { ageDays: 1004, l: -0.490601, m: 13.90242, s: 0.1102631 },
    { ageDays: 1035, l: -0.5292028, m: 14.04496, s: 0.1106879 },
    { ageDays: 1065, l: -0.5661908, m: 14.1847, s: 0.1111322 },
    { ageDays: 1096, l: -0.6036308, m: 14.33114, s: 0.1116248 },
    { ageDays: 1126, l: -0.638768, m: 14.47495, s: 0.112133 },
    { ageDays: 1157, l: -0.6736338, m: 14.62584, s: 0.1126897 },
    { ageDays: 1187, l: -0.7057373, m: 14.77413, s: 0.1132579 },
    { ageDays: 1218, l: -0.7370282, m: 14.92975, s: 0.1138743 },
    { ageDays: 1248, l: -0.7653563, m: 15.08267, s: 0.1144978 },
    { ageDays: 1278, l: -0.7916592, m: 15.23785, s: 0.1151466 },
    { ageDays: 1309, l: -0.8166686, m: 15.40056, s: 0.1158419 },
    { ageDays: 1339, l: -0.8387767, m: 15.56025, s: 0.1165375 },
    { ageDays: 1370, l: -0.8594909, m: 15.72751, s: 0.1172779 },
    { ageDays: 1400, l: -0.877536, m: 15.89147, s: 0.1180135 },
    { ageDays: 1431, l: -0.8942182, m: 16.06298, s: 0.1187917 },
    { ageDays: 1461, l: -0.9085833, m: 16.23089, s: 0.1195602 },
    { ageDays: 1491, l: -0.9213253, m: 16.40061, s: 0.1203421 },
    { ageDays: 1522, l: -0.9329382, m: 16.57778, s: 0.1211622 },
    { ageDays: 1552, l: -0.9428276, m: 16.75088, s: 0.1219657 },
    { ageDays: 1583, l: -0.9518196, m: 16.93135, s: 0.1228043 },
    { ageDays: 1613, l: -0.9594957, m: 17.10744, s: 0.1236219 },
    { ageDays: 1644, l: -0.9665352, m: 17.29079, s: 0.1244712 },
    { ageDays: 1674, l: -0.9726415, m: 17.46949, s: 0.1252958 },
    { ageDays: 1705, l: -0.9783757, m: 17.65535, s: 0.126149 },
    { ageDays: 1735, l: -0.9835166, m: 17.8363, s: 0.1269742 },
    { ageDays: 1765, l: -0.9883951, m: 18.01823, s: 0.1277975 },
    { ageDays: 1796, l: -0.9932883, m: 18.20718, s: 0.128645 },
    { ageDays: 1826, l: -0.9979991, m: 18.39089, s: 0.1294607 },
    { ageDays: 1857, l: -1.002955, m: 18.58154, s: 0.130298 },
    { ageDays: 1887, l: -1.007932, m: 18.76679, s: 0.1311018 },
    { ageDays: 1918, l: -1.013354, m: 18.95892, s: 0.131925 },
    { ageDays: 1948, l: -1.018945, m: 19.14549, s: 0.1327137 },
    { ageDays: 1978, l: -1.024938, m: 19.33266, s: 0.133494 },
    { ageDays: 2009, l: -1.031607, m: 19.52668, s: 0.1342911 },
    { ageDays: 2039, l: -1.038565, m: 19.715, s: 0.1350532 },
    { ageDays: 2070, l: -1.046308, m: 19.91016, s: 0.1358309 },
    { ageDays: 2100, l: -1.054356, m: 20.09956, s: 0.136574 },
    { ageDays: 2131, l: -1.063259, m: 20.29583, s: 0.137332 },
    { ageDays: 2161, l: -1.072436, m: 20.48632, s: 0.1380562 },
    { ageDays: 2192, l: -1.082495, m: 20.68375, s: 0.1387952 },
    { ageDays: 2222, l: -1.09277, m: 20.87538, s: 0.1395015 },
    { ageDays: 2252, l: -1.103546, m: 21.06762, s: 0.1401997 },
    { ageDays: 2283, l: -1.115171, m: 21.26693, s: 0.1409132 },
    { ageDays: 2313, l: -1.126853, m: 21.4605, s: 0.1415967 },
    { ageDays: 2344, l: -1.139315, m: 21.66127, s: 0.1422964 },
    { ageDays: 2374, l: -1.151697, m: 21.85635, s: 0.1429681 },
    { ageDays: 2405, l: -1.164759, m: 22.0588, s: 0.1436575 },
    { ageDays: 2435, l: -1.177587, m: 22.25563, s: 0.1443213 },
    { ageDays: 2465, l: -1.19053, m: 22.45341, s: 0.1449825 },
    { ageDays: 2496, l: -1.20395, m: 22.65885, s: 0.1456644 },
    { ageDays: 2526, l: -1.2169, m: 22.85878, s: 0.1463241 },
    { ageDays: 2557, l: -1.230163, m: 23.0666, s: 0.1470069 },
    { ageDays: 2587, l: -1.242799, m: 23.26899, s: 0.1476699 },
    { ageDays: 2618, l: -1.255568, m: 23.47952, s: 0.1483587 },
    { ageDays: 2648, l: -1.26757, m: 23.6847, s: 0.1490301 },
    { ageDays: 2679, l: -1.279522, m: 23.89831, s: 0.1497301 },
    { ageDays: 2709, l: -1.290579, m: 24.10663, s: 0.1504149 },
    { ageDays: 2739, l: -1.301065, m: 24.31663, s: 0.151108 },
    { ageDays: 2770, l: -1.311232, m: 24.53549, s: 0.1518343 },
    { ageDays: 2800, l: -1.320362, m: 24.74917, s: 0.1525479 },
    { ageDays: 2831, l: -1.329005, m: 24.97201, s: 0.1532975 },
    { ageDays: 2861, l: -1.336548, m: 25.18972, s: 0.1540358 },
    { ageDays: 2892, l: -1.343451, m: 25.41692, s: 0.154813 },
    { ageDays: 2922, l: -1.349231, m: 25.63902, s: 0.1555794 },
    { ageDays: 2952, l: -1.354091, m: 25.86342, s: 0.1563608 },
    { ageDays: 2983, l: -1.358121, m: 26.09778, s: 0.1571845 },
    { ageDays: 3013, l: -1.361039, m: 26.32709, s: 0.1579976 },
    { ageDays: 3044, l: -1.363028, m: 26.5667, s: 0.1588549 },
    { ageDays: 3074, l: -1.363951, m: 26.80125, s: 0.1597011 },
    { ageDays: 3105, l: -1.363869, m: 27.04644, s: 0.1605927 },
    { ageDays: 3135, l: -1.36279, m: 27.28655, s: 0.1614719 },
    { ageDays: 3166, l: -1.360655, m: 27.53766, s: 0.1623973 },
    { ageDays: 3196, l: -1.357615, m: 27.78364, s: 0.1633086 },
    { ageDays: 3226, l: -1.353635, m: 28.03262, s: 0.1642349 },
    { ageDays: 3257, l: -1.348559, m: 28.29312, s: 0.1652072 },
    { ageDays: 3287, l: -1.342743, m: 28.54841, s: 0.166162 },
    { ageDays: 3318, l: -1.335829, m: 28.81559, s: 0.1671618 },
    { ageDays: 3348, l: -1.328298, m: 29.07747, s: 0.1681411 },
    { ageDays: 3379, l: -1.319684, m: 29.3516, s: 0.169164 },
    { ageDays: 3409, l: -1.310582, m: 29.62035, s: 0.1701631 },
    { ageDays: 3439, l: -1.300764, m: 29.89256, s: 0.17117 },
    { ageDays: 3470, l: -1.289909, m: 30.17756, s: 0.1722169 },
    { ageDays: 3500, l: -1.278758, m: 30.45701, s: 0.1732348 },
    { ageDays: 3531, l: -1.266615, m: 30.7496, s: 0.1742896 },
    { ageDays: 3561, l: -1.254305, m: 31.03653, s: 0.1753116 },
    { ageDays: 3592, l: -1.241052, m: 31.33696, s: 0.1763669 },
    { ageDays: 3622, l: -1.227754, m: 31.63158, s: 0.1773856 },
    { ageDays: 3653, l: -1.213569, m: 31.94006, s: 0.1784335 },
    { ageDays: 3683, l: -1.199453, m: 32.24257, s: 0.179441 },
    { ageDays: 3713, l: -1.184997, m: 32.54903, s: 0.1804399 },
    { ageDays: 3744, l: -1.169743, m: 32.86991, s: 0.181461 },
    { ageDays: 3774, l: -1.154715, m: 33.18453, s: 0.1824363 },
    { ageDays: 3805, l: -1.13895, m: 33.51392, s: 0.1834286 },
    { ageDays: 3835, l: -1.123504, m: 33.83686, s: 0.1843718 },
    { ageDays: 3866, l: -1.107383, m: 34.17491, s: 0.1853266 },
    { ageDays: 3896, l: -1.091663, m: 34.50629, s: 0.1862292 },
    { ageDays: 3926, l: -1.075858, m: 34.84185, s: 0.1871089 },
    { ageDays: 3957, l: -1.059472, m: 35.19301, s: 0.1879917 },
    { ageDays: 3987, l: -1.04359, m: 35.53713, s: 0.1888187 },
    { ageDays: 4018, l: -1.027186, m: 35.89716, s: 0.1896431 },
    { ageDays: 4048, l: -1.011347, m: 36.24988, s: 0.1904097 },
    { ageDays: 4079, l: -0.9950447, m: 36.6188, s: 0.1911679 },
    { ageDays: 4109, l: -0.9793565, m: 36.98011, s: 0.1918671 },
    { ageDays: 4140, l: -0.9632626, m: 37.35788, s: 0.1925521 },
    { ageDays: 4170, l: -0.9478248, m: 37.72773, s: 0.1931773 },
    { ageDays: 4200, l: -0.932544, m: 38.10173, s: 0.193764 },
    { ageDays: 4231, l: -0.9169412, m: 38.49254, s: 0.1943284 },
    { ageDays: 4261, l: -0.9020437, m: 38.8749, s: 0.1948329 },
    { ageDays: 4292, l: -0.8868791, m: 39.27426, s: 0.1953101 },
    { ageDays: 4322, l: -0.8724449, m: 39.66478, s: 0.1957281 },
    { ageDays: 4353, l: -0.8577982, m: 40.07245, s: 0.1961141 },
    { ageDays: 4383, l: -0.8439016, m: 40.47087, s: 0.1964423 },
    { ageDays: 4413, l: -0.8302953, m: 40.87307, s: 0.1967254 },
    { ageDays: 4444, l: -0.8165577, m: 41.29254, s: 0.1969702 },
    { ageDays: 4474, l: -0.8035916, m: 41.70213, s: 0.1971604 },
    { ageDays: 4505, l: -0.7905496, m: 42.12902, s: 0.1973086 },
    { ageDays: 4535, l: -0.7782889, m: 42.54556, s: 0.1974053 },
    { ageDays: 4566, l: -0.7660088, m: 42.97938, s: 0.1974568 },
    { ageDays: 4596, l: -0.7545166, m: 43.40236, s: 0.1974603 },
    { ageDays: 4627, l: -0.7430633, m: 43.84254, s: 0.1974161 },
    { ageDays: 4657, l: -0.7324036, m: 44.27137, s: 0.1973278 },
    { ageDays: 4687, l: -0.722176, m: 44.70284, s: 0.1971952 },
    { ageDays: 4718, l: -0.7120777, m: 45.15128, s: 0.1970124 },
    { ageDays: 4748, l: -0.7027762, m: 45.58757, s: 0.1967921 },
    { ageDays: 4779, l: -0.6936684, m: 46.0406, s: 0.1965206 },
    { ageDays: 4809, l: -0.6853582, m: 46.48093, s: 0.1962165 },
    { ageDays: 4840, l: -0.677309, m: 46.9377, s: 0.1958609 },
    { ageDays: 4870, l: -0.6700569, m: 47.38122, s: 0.1954779 },
    { ageDays: 4900, l: -0.663349, m: 47.82595, s: 0.195058 },
    { ageDays: 4931, l: -0.6570074, m: 48.28656, s: 0.194587 },
    { ageDays: 4961, l: -0.6514592, m: 48.73307, s: 0.1940967 },
    { ageDays: 4992, l: -0.6463518, m: 49.19497, s: 0.1935561 },
    { ageDays: 5022, l: -0.6420317, m: 49.6422, s: 0.1930019 },
    { ageDays: 5053, l: -0.6382291, m: 50.1043, s: 0.1923989 },
    { ageDays: 5083, l: -0.6352058, m: 50.55117, s: 0.1917878 },
    { ageDays: 5114, l: -0.6327774, m: 51.01232, s: 0.1911298 },
    { ageDays: 5144, l: -0.6311166, m: 51.4577, s: 0.1904692 },
    { ageDays: 5174, l: -0.6301484, m: 51.90192, s: 0.189787 },
    { ageDays: 5205, l: -0.6298904, m: 52.35941, s: 0.1890615 },
    { ageDays: 5235, l: -0.6303723, m: 52.80036, s: 0.1883413 },
    { ageDays: 5266, l: -0.6316385, m: 53.25387, s: 0.1875805 },
    { ageDays: 5296, l: -0.6336174, m: 53.69036, s: 0.1868302 },
    { ageDays: 5327, l: -0.6364497, m: 54.13863, s: 0.1860422 },
    { ageDays: 5357, l: -0.6399587, m: 54.56944, s: 0.1852695 },
    { ageDays: 5387, l: -0.644227, m: 54.99701, s: 0.1844887 },
    { ageDays: 5418, l: -0.6494341, m: 55.43514, s: 0.1836752 },
    { ageDays: 5448, l: -0.6552455, m: 55.85526, s: 0.1828835 },
    { ageDays: 5479, l: -0.6620429, m: 56.28507, s: 0.1820626 },
    { ageDays: 5509, l: -0.6693754, m: 56.69658, s: 0.1812673 },
    { ageDays: 5540, l: -0.6777203, m: 57.11691, s: 0.1804465 },
    { ageDays: 5570, l: -0.6865223, m: 57.5187, s: 0.1796549 },
    { ageDays: 5601, l: -0.6963456, m: 57.92845, s: 0.1788413 },
    { ageDays: 5631, l: -0.7065311, m: 58.3195, s: 0.17806 },
    { ageDays: 5661, l: -0.717353, m: 58.7049, s: 0.177286 },
    { ageDays: 5692, l: -0.7291706, m: 59.097, s: 0.1764957 },
    { ageDays: 5722, l: -0.7411817, m: 59.47028, s: 0.1757413 },
    { ageDays: 5753, l: -0.7541378, m: 59.84944, s: 0.1749741 },
    { ageDays: 5783, l: -0.7671585, m: 60.20981, s: 0.1742449 },
    { ageDays: 5814, l: -0.7810537, m: 60.57526, s: 0.1735064 },
    { ageDays: 5844, l: -0.7948666, m: 60.92206, s: 0.1728074 },
    { ageDays: 5874, l: -0.8089815, m: 61.26198, s: 0.1721249 },
    { ageDays: 5905, l: -0.8238167, m: 61.60587, s: 0.1714382 },
    { ageDays: 5935, l: -0.8383474, m: 61.93145, s: 0.1707925 },
    { ageDays: 5966, l: -0.8534688, m: 62.26035, s: 0.1701456 },
    { ageDays: 5996, l: -0.8681332, m: 62.57126, s: 0.16954 },
    { ageDays: 6027, l: -0.8832414, m: 62.88487, s: 0.1689361 },
    { ageDays: 6057, l: -0.8977441, m: 63.18093, s: 0.1683734 },
    { ageDays: 6088, l: -0.9125305, m: 63.47916, s: 0.1678149 },
    { ageDays: 6118, l: -0.926573, m: 63.76032, s: 0.1672969 },
    { ageDays: 6148, l: -0.9402812, m: 64.03418, s: 0.1668013 },
    { ageDays: 6179, l: -0.9540221, m: 64.30956, s: 0.1663129 },
    { ageDays: 6209, l: -0.9668406, m: 64.56876, s: 0.1658632 },
    { ageDays: 6240, l: -0.9795228, m: 64.82912, s: 0.165422 },
    { ageDays: 6270, l: -0.9911884, m: 65.07397, s: 0.1650176 },
    { ageDays: 6301, l: -1.002551, m: 65.31976, s: 0.1646227 },
    { ageDays: 6331, l: -1.012828, m: 65.55075, s: 0.1642621 },
    { ageDays: 6361, l: -1.02235, m: 65.77515, s: 0.1639222 },
    { ageDays: 6392, l: -1.031345, m: 66.00029, s: 0.163592 },
    { ageDays: 6422, l: -1.039195, m: 66.21183, s: 0.1632919 },
    { ageDays: 6453, l: -1.046385, m: 66.4241, s: 0.1630011 },
    { ageDays: 6483, l: -1.052421, m: 66.62363, s: 0.1627372 },
    { ageDays: 6514, l: -1.05768, m: 66.82394, s: 0.1624816 },
    { ageDays: 6544, l: -1.061803, m: 67.0124, s: 0.1622496 },
    { ageDays: 6575, l: -1.065047, m: 67.2018, s: 0.1620247 },
    { ageDays: 6605, l: -1.067195, m: 67.38023, s: 0.1618202 },
    { ageDays: 6635, l: -1.068367, m: 67.55412, s: 0.1616274 },
    { ageDays: 6666, l: -1.068549, m: 67.72935, s: 0.1614395 },
    { ageDays: 6696, l: -1.067732, m: 67.89488, s: 0.1612678 },
    { ageDays: 6727, l: -1.065875, m: 68.06205, s: 0.1610998 },
    { ageDays: 6757, l: -1.063114, m: 68.22034, s: 0.1609456 },
    { ageDays: 6788, l: -1.059284, m: 68.38057, s: 0.1607948 },
    { ageDays: 6818, l: -1.054664, m: 68.53264, s: 0.1606567 },
    { ageDays: 6848, l: -1.049182, m: 68.68198, s: 0.1605264 },
    { ageDays: 6879, l: -1.042651, m: 68.83363, s: 0.1604006 },
    { ageDays: 6909, l: -1.035543, m: 68.97795, s: 0.1602884 },
    { ageDays: 6940, l: -1.02745, m: 69.12468, s: 0.1601839 },
    { ageDays: 6970, l: -1.018965, m: 69.2644, s: 0.1600958 },
    { ageDays: 7001, l: -1.009611, m: 69.4064, s: 0.1600211 },
    { ageDays: 7031, l: -1.000092, m: 69.54144, s: 0.1599682 },
    { ageDays: 7062, l: -0.9898911, m: 69.67831, s: 0.1599382 },
    { ageDays: 7092, l: -0.9797986, m: 69.80794, s: 0.1599381 },
    { ageDays: 7122, l: -0.9696347, m: 69.93438, s: 0.159973 },
    { ageDays: 7153, l: -0.9592283, m: 70.06117, s: 0.1600536 },
    { ageDays: 7183, l: -0.949433, m: 70.1795, s: 0.1601836 },
    { ageDays: 7214, l: -0.9398011, m: 70.29641, s: 0.1603825 },
    { ageDays: 7244, l: -0.931157, m: 70.40342, s: 0.1606495 },
    { ageDays: 7275, l: -0.9231451, m: 70.50647, s: 0.1610169 },
    { ageDays: 7305, l: -0.9164876, m: 70.59762, s: 0.1614768 },
  ],
  female: [
    { ageDays: 731, l: -0.7358937, m: 12.05765, s: 0.1074107 },
    { ageDays: 761, l: -0.7685648, m: 12.21351, s: 0.1081013 },
    { ageDays: 791, l: -0.7990584, m: 12.36627, s: 0.1088611 },
    { ageDays: 822, l: -0.8284314, m: 12.52162, s: 0.109711 },
    { ageDays: 852, l: -0.8549259, m: 12.67014, s: 0.110589 },
    { ageDays: 883, l: -0.8804531, m: 12.82232, s: 0.1115464 },
    { ageDays: 913, l: -0.9035088, m: 12.96881, s: 0.1125146 },
    { ageDays: 944, l: -0.9257747, m: 13.11982, s: 0.1135511 },
    { ageDays: 974, l: -0.9459562, m: 13.26598, s: 0.1145828 },
    { ageDays: 1004, l: -0.9649258, m: 13.41246, s: 0.1156365 },
    { ageDays: 1035, l: -0.9833792, m: 13.56446, s: 0.1167426 },
    { ageDays: 1065, l: -1.00025, m: 13.7124, s: 0.1178242 },
    { ageDays: 1096, l: -1.016783, m: 13.86632, s: 0.1189482 },
    { ageDays: 1126, l: -1.032015, m: 14.01647, s: 0.1200372 },
    { ageDays: 1157, l: -1.047059, m: 14.17298, s: 0.1211596 },
    { ageDays: 1187, l: -1.061029, m: 14.32586, s: 0.1222389 },
    { ageDays: 1218, l: -1.074937, m: 14.48537, s: 0.1233436 },
    { ageDays: 1248, l: -1.087954, m: 14.64128, s: 0.1243994 },
    { ageDays: 1278, l: -1.100589, m: 14.79875, s: 0.1254395 },
    { ageDays: 1309, l: -1.113294, m: 14.96311, s: 0.1264956 },
    { ageDays: 1339, l: -1.125289, m: 15.12376, s: 0.1274977 },
    { ageDays: 1370, l: -1.137404, m: 15.29142, s: 0.1285112 },
    { ageDays: 1400, l: -1.148881, m: 15.45524, s: 0.1294696 },
    { ageDays: 1431, l: -1.160501, m: 15.62613, s: 0.1304363 },
    { ageDays: 1461, l: -1.171523, m: 15.79302, s: 0.1313483 },
    { ageDays: 1491, l: -1.182323, m: 15.96136, s: 0.1322374 },
    { ageDays: 1522, l: -1.19325, m: 16.13681, s: 0.1331321 },
    { ageDays: 1552, l: -1.203594, m: 16.308, s: 0.1339753 },
    { ageDays: 1583, l: -1.214026, m: 16.4863, s: 0.1348241 },
    { ageDays: 1613, l: -1.223857, m: 16.66016, s: 0.1356246 },
    { ageDays: 1644, l: -1.233721, m: 16.84115, s: 0.1364313 },
    { ageDays: 1674, l: -1.242958, m: 17.01752, s: 0.1371937 },
    { ageDays: 1705, l: -1.252156, m: 17.20103, s: 0.1379641 },
    { ageDays: 1735, l: -1.260695, m: 17.37979, s: 0.1386944 },
    { ageDays: 1765, l: -1.268849, m: 17.55967, s: 0.1394114 },
    { ageDays: 1796, l: -1.27684, m: 17.74671, s: 0.1401401 },
    { ageDays: 1826, l: -1.284121, m: 17.92882, s: 0.1408355 },
    { ageDays: 1857, l: -1.291145, m: 18.11813, s: 0.1415458 },
    { ageDays: 1887, l: -1.29743, m: 18.30242, s: 0.1422271 },
    { ageDays: 1918, l: -1.303367, m: 18.49397, s: 0.142927 },
    { ageDays: 1948, l: -1.308543, m: 18.68044, s: 0.143602 },
    { ageDays: 1978, l: -1.313134, m: 18.86799, s: 0.1442768 },
    { ageDays: 2009, l: -1.317238, m: 19.06295, s: 0.1449756 },
    { ageDays: 2039, l: -1.320567, m: 19.25277, s: 0.1456552 },
    { ageDays: 2070, l: -1.323322, m: 19.45013, s: 0.1463628 },
    { ageDays: 2100, l: -1.325309, m: 19.64233, s: 0.1470542 },
    { ageDays: 2131, l: -1.326644, m: 19.84223, s: 0.1477773 },
    { ageDays: 2161, l: -1.327228, m: 20.03699, s: 0.1484869 },
    { ageDays: 2192, l: -1.327093, m: 20.23963, s: 0.1492317 },
    { ageDays: 2222, l: -1.32624, m: 20.43715, s: 0.1499649 },
    { ageDays: 2252, l: -1.324675, m: 20.63612, s: 0.1507115 },
    { ageDays: 2283, l: -1.322309, m: 20.84331, s: 0.1514982 },
    { ageDays: 2313, l: -1.319297, m: 21.04544, s: 0.152275 },
    { ageDays: 2344, l: -1.315447, m: 21.25604, s: 0.1530946 },
    { ageDays: 2374, l: -1.311017, m: 21.46163, s: 0.1539048 },
    { ageDays: 2405, l: -1.305724, m: 21.67599, s: 0.15476 },
    { ageDays: 2435, l: -1.299924, m: 21.88537, s: 0.1556056 },
    { ageDays: 2465, l: -1.293478, m: 22.09675, s: 0.156469 },
    { ageDays: 2496, l: -1.286158, m: 22.31737, s: 0.15738 },
    { ageDays: 2526, l: -1.27846, m: 22.53308, s: 0.1582799 },
    { ageDays: 2557, l: -1.269896, m: 22.75836, s: 0.1592284 },
    { ageDays: 2587, l: -1.261048, m: 22.97878, s: 0.1601642 },
    { ageDays: 2618, l: -1.251358, m: 23.20913, s: 0.1611491 },
    { ageDays: 2648, l: -1.241479, m: 23.43464, s: 0.162119 },
    { ageDays: 2679, l: -1.230788, m: 23.67044, s: 0.1631381 },
    { ageDays: 2709, l: -1.220009, m: 23.90142, s: 0.1641396 },
    { ageDays: 2739, l: -1.208836, m: 24.13522, s: 0.1651555 },
    { ageDays: 2770, l: -1.196914, m: 24.37987, s: 0.1662192 },
    { ageDays: 2800, l: -1.18505, m: 24.61968, s: 0.1672609 },
    { ageDays: 2831, l: -1.172491, m: 24.87073, s: 0.168349 },
    { ageDays: 2861, l: -1.160084, m: 25.11689, s: 0.1694119 },
    { ageDays: 2892, l: -1.14704, m: 25.37466, s: 0.1705191 },
    { ageDays: 2922, l: -1.134236, m: 25.62748, s: 0.1715977 },
    { ageDays: 2952, l: -1.12129, m: 25.88368, s: 0.1726818 },
    { ageDays: 2983, l: -1.107802, m: 26.15203, s: 0.1738061 },
    { ageDays: 3013, l: -1.094676, m: 26.41529, s: 0.1748966 },
    { ageDays: 3044, l: -1.081073, m: 26.69106, s: 0.1760241 },
    { ageDays: 3074, l: -1.067905, m: 26.96159, s: 0.1771143 },
    { ageDays: 3105, l: -1.054328, m: 27.24496, s: 0.1782378 },
    { ageDays: 3135, l: -1.041249, m: 27.52292, s: 0.1793206 },
    { ageDays: 3166, l: -1.027831, m: 27.81404, s: 0.1804329 },
    { ageDays: 3196, l: -1.014966, m: 28.09955, s: 0.1815011 },
    { ageDays: 3226, l: -1.00225, m: 28.38878, s: 0.1825595 },
    { ageDays: 3257, l: -0.9892936, m: 28.69158, s: 0.183641 },
    { ageDays: 3287, l: -0.9769592, m: 28.98841, s: 0.1846741 },
    { ageDays: 3318, l: -0.9644507, m: 29.29903, s: 0.1857258 },
    { ageDays: 3348, l: -0.9525988, m: 29.60339, s: 0.1867266 },
    { ageDays: 3379, l: -0.9406372, m: 29.92176, s: 0.1877413 },
    { ageDays: 3409, l: -0.929359, m: 30.23354, s: 0.1887029 },
    { ageDays: 3439, l: -0.9183936, m: 30.54891, s: 0.1896429 },
    { ageDays: 3470, l: -0.907411, m: 30.8785, s: 0.1905898 },
    { ageDays: 3500, l: -0.8971381, m: 31.20098, s: 0.1914811 },
    { ageDays: 3531, l: -0.8869076, m: 31.53778, s: 0.1923746 },
    { ageDays: 3561, l: -0.8773959, m: 31.86708, s: 0.1932115 },
    { ageDays: 3592, l: -0.8679845, m: 32.21074, s: 0.1940459 },
    { ageDays: 3622, l: -0.859295, m: 32.54649, s: 0.194823 },
    { ageDays: 3653, l: -0.8507618, m: 32.8966, s: 0.1955933 },
    { ageDays: 3683, l: -0.842948, m: 33.23836, s: 0.1963059 },
    { ageDays: 3713, l: -0.8355827, m: 33.5829, s: 0.1969853 },
    { ageDays: 3744, l: -0.8284543, m: 33.94169, s: 0.1976515 },
    { ageDays: 3774, l: -0.8220331, m: 34.29145, s: 0.1982605 },
    { ageDays: 3805, l: -0.8159012, m: 34.65533, s: 0.1988523 },
    { ageDays: 3835, l: -0.8104634, m: 35.00972, s: 0.1993879 },
    { ageDays: 3866, l: -0.8053649, m: 35.37804, s: 0.1999024 },
    { ageDays: 3896, l: -0.8009441, m: 35.73637, s: 0.200362 },
    { ageDays: 3926, l: -0.7970369, m: 36.09638, s: 0.2007835 },
    { ageDays: 3957, l: -0.7935452, m: 36.46995, s: 0.2011786 },
    { ageDays: 3987, l: -0.7907001, m: 36.83281, s: 0.2015216 },
    { ageDays: 4018, l: -0.7883186, m: 37.20892, s: 0.2018349 },
    { ageDays: 4048, l: -0.7865619, m: 37.57382, s: 0.2020984 },
    { ageDays: 4079, l: -0.785318, m: 37.95161, s: 0.2023293 },
    { ageDays: 4109, l: -0.7846731, m: 38.3177, s: 0.2025128 },
    { ageDays: 4140, l: -0.7845897, m: 38.69628, s: 0.2026612 },
    { ageDays: 4170, l: -0.7850784, m: 39.06269, s: 0.202765 },
    { ageDays: 4200, l: -0.7861318, m: 39.42892, s: 0.2028297 },
    { ageDays: 4231, l: -0.7878194, m: 39.80694, s: 0.2028559 },
    { ageDays: 4261, l: -0.7900354, m: 40.17212, s: 0.2028421 },
    { ageDays: 4292, l: -0.792932, m: 40.54857, s: 0.2027878 },
    { ageDays: 4322, l: -0.7963279, m: 40.91175, s: 0.2026968 },
    { ageDays: 4353, l: -0.8004526, m: 41.28565, s: 0.2025635 },
    { ageDays: 4383, l: -0.8050439, m: 41.6459, s: 0.2023971 },
    { ageDays: 4413, l: -0.8102283, m: 42.00435, s: 0.2021944 },
    { ageDays: 4444, l: -0.8162119, m: 42.37263, s: 0.2019474 },
    { ageDays: 4474, l: -0.8226117, m: 42.72673, s: 0.2016727 },
    { ageDays: 4505, l: -0.8298569, m: 43.09002, s: 0.2013526 },
    { ageDays: 4535, l: -0.8374829, m: 43.43883, s: 0.2010085 },
    { ageDays: 4566, l: -0.8459999, m: 43.79619, s: 0.2006183 },
    { ageDays: 4596, l: -0.85486, m: 44.1388, s: 0.2002079 },
    { ageDays: 4627, l: -0.8646557, m: 44.48929, s: 0.1997508 },
    { ageDays: 4657, l: -0.8747547, m: 44.82482, s: 0.1992773 },
    { ageDays: 4687, l: -0.8854635, m: 45.15657, s: 0.1987741 },
    { ageDays: 4718, l: -0.8971695, m: 45.49518, s: 0.1982238 },
    { ageDays: 4748, l: -0.9091164, m: 45.81862, s: 0.197663 },
    { ageDays: 4779, l: -0.9220979, m: 46.14823, s: 0.1970551 },
    { ageDays: 4809, l: -0.9352742, m: 46.46259, s: 0.1964405 },
    { ageDays: 4840, l: -0.9495207, m: 46.78245, s: 0.1957792 },
    { ageDays: 4870, l: -0.9639133, m: 47.08703, s: 0.195115 },
    { ageDays: 4900, l: -0.978896, m: 47.38656, s: 0.194428 },
    { ageDays: 4931, l: -0.9949912, m: 47.69062, s: 0.1936953 },
    { ageDays: 4961, l: -1.011152, m: 47.97945, s: 0.1929653 },
    { ageDays: 4992, l: -1.028447, m: 48.27219, s: 0.1921905 },
    { ageDays: 5022, l: -1.04575, m: 48.54981, s: 0.1914222 },
    { ageDays: 5053, l: -1.064199, m: 48.83071, s: 0.1906105 },
    { ageDays: 5083, l: -1.082593, m: 49.09669, s: 0.1898089 },
    { ageDays: 5114, l: -1.102139, m: 49.36539, s: 0.1889653 },
    { ageDays: 5144, l: -1.12156, m: 49.61939, s: 0.1881356 },
    { ageDays: 5174, l: -1.141458, m: 49.8674, s: 0.187294 },
    { ageDays: 5205, l: -1.162498, m: 50.11735, s: 0.1864133 },
    { ageDays: 5235, l: -1.183299, m: 50.35306, s: 0.1855517 },
    { ageDays: 5266, l: -1.20522, m: 50.59023, s: 0.1846533 },
    { ageDays: 5296, l: -1.226821, m: 50.81355, s: 0.1837775 },
    { ageDays: 5327, l: -1.249507, m: 51.03791, s: 0.1828674 },
    { ageDays: 5357, l: -1.271783, m: 51.24884, s: 0.1819833 },
    { ageDays: 5387, l: -1.294341, m: 51.45372, s: 0.1810974 },
    { ageDays: 5418, l: -1.317906, m: 51.65911, s: 0.1801816 },
    { ageDays: 5448, l: -1.340918, m: 51.85181, s: 0.1792967 },
    { ageDays: 5479, l: -1.364867, m: 52.04475, s: 0.1783853 },
    { ageDays: 5509, l: -1.388162, m: 52.22555, s: 0.1775078 },
    { ageDays: 5540, l: -1.412305, m: 52.40635, s: 0.1766074 },
    { ageDays: 5570, l: -1.43569, m: 52.57561, s: 0.1757437 },
    { ageDays: 5601, l: -1.459821, m: 52.74472, s: 0.1748611 },
    { ageDays: 5631, l: -1.483086, m: 52.90289, s: 0.174018 },
    { ageDays: 5661, l: -1.50621, m: 53.05581, s: 0.1731873 },
    { ageDays: 5692, l: -1.529897, m: 53.20846, s: 0.1723437 },
    { ageDays: 5722, l: -1.552558, m: 53.35115, s: 0.1715433 },
    { ageDays: 5753, l: -1.575641, m: 53.49357, s: 0.1707343 },
    { ageDays: 5783, l: -1.597595, m: 53.6267, s: 0.1699705 },
    { ageDays: 5814, l: -1.619817, m: 53.75961, s: 0.1692026 },
    { ageDays: 5844, l: -1.640811, m: 53.88393, s: 0.1684815 },
    { ageDays: 5874, l: -1.661239, m: 54.0042, s: 0.1677837 },
    { ageDays: 5905, l: -1.681687, m: 54.12445, s: 0.1670884 },
    { ageDays: 5935, l: -1.700774, m: 54.23713, s: 0.1664419 },
    { ageDays: 5966, l: -1.719708, m: 54.34998, s: 0.1658024 },
    { ageDays: 5996, l: -1.737205, m: 54.45596, s: 0.1652124 },
    { ageDays: 6027, l: -1.754369, m: 54.56234, s: 0.1646337 },
    { ageDays: 6057, l: -1.770037, m: 54.66252, s: 0.1641047 },
    { ageDays: 6088, l: -1.785195, m: 54.7634, s: 0.1635912 },
    { ageDays: 6118, l: -1.798811, m: 54.85873, s: 0.1631273 },
    { ageDays: 6148, l: -1.811345, m: 54.95202, s: 0.1626964 },
    { ageDays: 6179, l: -1.82311, m: 55.04652, s: 0.1622868 },
    { ageDays: 6209, l: -1.833305, m: 55.13637, s: 0.1619254 },
    { ageDays: 6240, l: -1.842569, m: 55.22782, s: 0.1615885 },
    { ageDays: 6270, l: -1.850272, m: 55.31518, s: 0.1612984 },
    { ageDays: 6301, l: -1.856895, m: 55.40452, s: 0.1610357 },
    { ageDays: 6331, l: -1.861987, m: 55.4903, s: 0.1608177 },
    { ageDays: 6361, l: -1.865764, m: 55.57561, s: 0.1606351 },
    { ageDays: 6392, l: -1.868272, m: 55.66349, s: 0.1604835 },
    { ageDays: 6422, l: -1.869342, m: 55.74846, s: 0.1603725 },
    { ageDays: 6453, l: -1.869042, m: 55.83638, s: 0.160294 },
    { ageDays: 6483, l: -1.867395, m: 55.92174, s: 0.1602528 },
    { ageDays: 6514, l: -1.864303, m: 56.01039, s: 0.1602451 },
    { ageDays: 6544, l: -1.859984, m: 56.09676, s: 0.1602706 },
    { ageDays: 6575, l: -1.854174, m: 56.18671, s: 0.16033 },
    { ageDays: 6605, l: -1.847281, m: 56.27454, s: 0.1604182 },
    { ageDays: 6635, l: -1.839174, m: 56.3632, s: 0.1605355 },
    { ageDays: 6666, l: -1.82957, m: 56.45574, s: 0.1606855 },
    { ageDays: 6696, l: -1.819139, m: 56.54621, s: 0.1608572 },
    { ageDays: 6727, l: -1.807251, m: 56.64061, s: 0.16106 },
    { ageDays: 6757, l: -1.794738, m: 56.73281, s: 0.1612791 },
    { ageDays: 6788, l: -1.780846, m: 56.82887, s: 0.1615269 },
    { ageDays: 6818, l: -1.766551, m: 56.92245, s: 0.1617855 },
    { ageDays: 6848, l: -1.751508, m: 57.01647, s: 0.1620603 },
    { ageDays: 6879, l: -1.735277, m: 57.11388, s: 0.1623589 },
    { ageDays: 6909, l: -1.719012, m: 57.20812, s: 0.1626598 },
    { ageDays: 6940, l: -1.70174, m: 57.30515, s: 0.1629805 },
    { ageDays: 6970, l: -1.684694, m: 57.39834, s: 0.1632978 },
    { ageDays: 7001, l: -1.666871, m: 57.49344, s: 0.1636305 },
    { ageDays: 7031, l: -1.649553, m: 57.58384, s: 0.1639545 },
    { ageDays: 7062, l: -1.631727, m: 57.675, s: 0.164289 },
    { ageDays: 7092, l: -1.61469, m: 57.76039, s: 0.1646101 },
    { ageDays: 7122, l: -1.598011, m: 57.84234, s: 0.1649264 },
    { ageDays: 7153, l: -1.581315, m: 57.92262, s: 0.1652461 },
    { ageDays: 7183, l: -1.56584, m: 57.99522, s: 0.1655466 },
    { ageDays: 7214, l: -1.550735, m: 58.06401, s: 0.165846 },
    { ageDays: 7244, l: -1.537146, m: 58.12358, s: 0.1661234 },
    { ageDays: 7275, l: -1.524351, m: 58.17676, s: 0.1663959 },
    { ageDays: 7305, l: -1.513362, m: 58.21897, s: 0.1666448 },
  ],
};

/** Stature-for-age, 24 months through 20 years, monthly LMS values. */
export const CDC_LENGTH_HEIGHT_CM_REFERENCE: Readonly<
  Record<'male' | 'female', readonly CdcReferenceRow[]>
> = {
  male: [
    { ageDays: 731, l: 0.943682, m: 86.46565, s: 0.04032396 },
    { ageDays: 761, l: 0.9512601, m: 87.26247, s: 0.0404854 },
    { ageDays: 791, l: 0.7595889, m: 88.03078, s: 0.04065302 },
    { ageDays: 822, l: 0.6077155, m: 88.80625, s: 0.04078319 },
    { ageDays: 852, l: 0.4729538, m: 89.53901, s: 0.04087467 },
    { ageDays: 883, l: 0.3448137, m: 90.27909, s: 0.04093513 },
    { ageDays: 913, l: 0.2300938, m: 90.97972, s: 0.0409625 },
    { ageDays: 944, l: 0.1196394, m: 91.68851, s: 0.04096081 },
    { ageDays: 974, l: 0.01920487, m: 92.3605, s: 0.04093244 },
    { ageDays: 1004, l: -0.0761357, m: 93.01949, s: 0.0408801 },
    { ageDays: 1035, l: -0.1705166, m: 93.68752, s: 0.04080336 },
    { ageDays: 1065, l: -0.2589107, m: 94.32216, s: 0.0407096 },
    { ageDays: 1096, l: -0.3622162, m: 94.96493, s: 0.04058424 },
    { ageDays: 1126, l: -0.3372684, m: 95.58988, s: 0.04054296 },
    { ageDays: 1157, l: -0.1877855, m: 96.23988, s: 0.04059476 },
    { ageDays: 1187, l: -0.06384575, m: 96.85931, s: 0.04064087 },
    { ageDays: 1218, l: 0.05756859, m: 97.49149, s: 0.04069416 },
    { ageDays: 1248, l: 0.1688281, m: 98.0961, s: 0.0407512 },
    { ageDays: 1278, l: 0.2741143, m: 98.69414, s: 0.04081357 },
    { ageDays: 1309, l: 0.3767848, m: 99.30567, s: 0.04088346 },
    { ageDays: 1339, l: 0.4703259, m: 99.89169, s: 0.04095617 },
    { ageDays: 1370, l: 0.5610728, m: 100.4917, s: 0.04103627 },
    { ageDays: 1400, l: 0.6432555, m: 101.0674, s: 0.04111829 },
    { ageDays: 1431, l: 0.7224354, m: 101.6575, s: 0.04120734 },
    { ageDays: 1461, l: 0.7935812, m: 102.2244, s: 0.04129731 },
    { ageDays: 1491, l: 0.8594137, m: 102.7875, s: 0.04139063 },
    { ageDays: 1522, l: 0.9219431, m: 103.3656, s: 0.04149017 },
    { ageDays: 1552, l: 0.9772118, m: 103.9219, s: 0.04158907 },
    { ageDays: 1583, l: 1.029017, m: 104.4937, s: 0.0416935 },
    { ageDays: 1613, l: 1.074111, m: 105.0443, s: 0.04179629 },
    { ageDays: 1644, l: 1.115592, m: 105.6108, s: 0.04190386 },
    { ageDays: 1674, l: 1.150906, m: 106.1569, s: 0.04200885 },
    { ageDays: 1705, l: 1.182527, m: 106.7192, s: 0.04211783 },
    { ageDays: 1735, l: 1.208539, m: 107.2615, s: 0.04222338 },
    { ageDays: 1765, l: 1.230164, m: 107.8024, s: 0.04232864 },
    { ageDays: 1796, l: 1.248042, m: 108.3598, s: 0.04243674 },
    { ageDays: 1826, l: 1.261162, m: 108.898, s: 0.04254036 },
    { ageDays: 1857, l: 1.270552, m: 109.4531, s: 0.04264609 },
    { ageDays: 1887, l: 1.275759, m: 109.9892, s: 0.0427468 },
    { ageDays: 1918, l: 1.277294, m: 110.5423, s: 0.04284894 },
    { ageDays: 1948, l: 1.275223, m: 111.0768, s: 0.04294568 },
    { ageDays: 1978, l: 1.269818, m: 111.6107, s: 0.04304016 },
    { ageDays: 2009, l: 1.260908, m: 112.1616, s: 0.04313522 },
    { ageDays: 2039, l: 1.249245, m: 112.6941, s: 0.04322459 },
    { ageDays: 2070, l: 1.234241, m: 113.2438, s: 0.04331411 },
    { ageDays: 2100, l: 1.217051, m: 113.7752, s: 0.04339794 },
    { ageDays: 2131, l: 1.196731, m: 114.3237, s: 0.0434816 },
    { ageDays: 2161, l: 1.174781, m: 114.854, s: 0.0435597 },
    { ageDays: 2192, l: 1.149941, m: 115.4013, s: 0.04363747 },
    { ageDays: 2222, l: 1.124021, m: 115.9303, s: 0.04370993 },
    { ageDays: 2252, l: 1.096452, m: 116.4586, s: 0.04377973 },
    { ageDays: 2283, l: 1.066444, m: 117.0037, s: 0.04384919 },
    { ageDays: 2313, l: 1.036143, m: 117.5305, s: 0.04391395 },
    { ageDays: 2344, l: 1.003749, m: 118.0739, s: 0.04397852 },
    { ageDays: 2374, l: 0.9715646, m: 118.5988, s: 0.04403894 },
    { ageDays: 2405, l: 0.9376642, m: 119.14, s: 0.04409944 },
    { ageDays: 2435, l: 0.9044459, m: 119.6626, s: 0.04415637 },
    { ageDays: 2465, l: 0.8710287, m: 120.1839, s: 0.04421196 },
    { ageDays: 2496, l: 0.8365015, m: 120.7211, s: 0.04426828 },
    { ageDays: 2526, l: 0.8032942, m: 121.2395, s: 0.04432198 },
    { ageDays: 2557, l: 0.7693988, m: 121.7734, s: 0.04437694 },
    { ageDays: 2587, l: 0.7371927, m: 122.2883, s: 0.04442991 },
    { ageDays: 2618, l: 0.7047201, m: 122.8183, s: 0.04448475 },
    { ageDays: 2648, l: 0.6742491, m: 123.3292, s: 0.04453821 },
    { ageDays: 2679, l: 0.6439304, m: 123.8548, s: 0.04459418 },
    { ageDays: 2709, l: 0.6158658, m: 124.3611, s: 0.04464934 },
    { ageDays: 2739, l: 0.5891854, m: 124.865, s: 0.04470575 },
    { ageDays: 2770, l: 0.5632086, m: 125.383, s: 0.04476563 },
    { ageDays: 2800, l: 0.5397229, m: 125.8815, s: 0.04482537 },
    { ageDays: 2831, l: 0.5172634, m: 126.3936, s: 0.0448892 },
    { ageDays: 2861, l: 0.497359, m: 126.8863, s: 0.04495323 },
    { ageDays: 2892, l: 0.4787492, m: 127.392, s: 0.04502192 },
    { ageDays: 2922, l: 0.4626802, m: 127.8783, s: 0.04509101 },
    { ageDays: 2952, l: 0.4485479, m: 128.3612, s: 0.04516281 },
    { ageDays: 2983, l: 0.4359896, m: 128.8567, s: 0.04523998 },
    { ageDays: 3013, l: 0.4258084, m: 129.3327, s: 0.04531762 },
    { ageDays: 3044, l: 0.4172993, m: 129.8207, s: 0.04540097 },
    { ageDays: 3074, l: 0.4109692, m: 130.2894, s: 0.0454847 },
    { ageDays: 3105, l: 0.4063345, m: 130.7697, s: 0.04557439 },
    { ageDays: 3135, l: 0.4036192, m: 131.2307, s: 0.04566421 },
    { ageDays: 3166, l: 0.4025483, m: 131.7032, s: 0.04576012 },
    { ageDays: 3196, l: 0.4030867, m: 132.1565, s: 0.04585586 },
    { ageDays: 3226, l: 0.405059, m: 132.606, s: 0.04595435 },
    { ageDays: 3257, l: 0.4084704, m: 133.0664, s: 0.04605891 },
    { ageDays: 3287, l: 0.4129618, m: 133.5082, s: 0.04616263 },
    { ageDays: 3318, l: 0.4186781, m: 133.9607, s: 0.04627225 },
    { ageDays: 3348, l: 0.4250952, m: 134.3949, s: 0.04638052 },
    { ageDays: 3379, l: 0.4324726, m: 134.8397, s: 0.04649444 },
    { ageDays: 3409, l: 0.440169, m: 135.2666, s: 0.04660645 },
    { ageDays: 3439, l: 0.4482482, m: 135.69, s: 0.04671998 },
    { ageDays: 3470, l: 0.4568244, m: 136.1241, s: 0.04683863 },
    { ageDays: 3500, l: 0.4651765, m: 136.541, s: 0.04695451 },
    { ageDays: 3531, l: 0.4736884, m: 136.9687, s: 0.04707508 },
    { ageDays: 3561, l: 0.4816497, m: 137.3798, s: 0.04719232 },
    { ageDays: 3592, l: 0.489429, m: 137.802, s: 0.04731377 },
    { ageDays: 3622, l: 0.4963769, m: 138.2082, s: 0.04743136 },
    { ageDays: 3653, l: 0.5028123, m: 138.6258, s: 0.04755266 },
    { ageDays: 3683, l: 0.5081932, m: 139.0282, s: 0.04766961 },
    { ageDays: 3713, l: 0.5126289, m: 139.4292, s: 0.04778591 },
    { ageDays: 3744, l: 0.516117, m: 139.8426, s: 0.04790517 },
    { ageDays: 3774, l: 0.51835, m: 140.2419, s: 0.04801948 },
    { ageDays: 3805, l: 0.51941, m: 140.6543, s: 0.04813627 },
    { ageDays: 3835, l: 0.5191854, m: 141.0537, s: 0.04824782 },
    { ageDays: 3866, l: 0.5176394, m: 141.467, s: 0.04836139 },
    { ageDays: 3896, l: 0.5148758, m: 141.8683, s: 0.04846951 },
    { ageDays: 3926, l: 0.5108951, m: 142.2712, s: 0.04857575 },
    { ageDays: 3957, l: 0.5055637, m: 142.69, s: 0.04868342 },
    { ageDays: 3987, l: 0.4993143, m: 143.0981, s: 0.0487855 },
    { ageDays: 4018, l: 0.4918565, m: 143.5235, s: 0.04888871 },
    { ageDays: 4048, l: 0.4838254, m: 143.9392, s: 0.04898632 },
    { ageDays: 4079, l: 0.4748827, m: 144.3738, s: 0.04908479 },
    { ageDays: 4109, l: 0.4658288, m: 144.7998, s: 0.04917774 },
    { ageDays: 4140, l: 0.4563305, m: 145.2463, s: 0.04927133 },
    { ageDays: 4170, l: 0.4472932, m: 145.6852, s: 0.0493595 },
    { ageDays: 4200, l: 0.438728, m: 146.1315, s: 0.04944529 },
    { ageDays: 4231, l: 0.4307401, m: 146.601, s: 0.0495314 },
    { ageDays: 4261, l: 0.424226, m: 147.0642, s: 0.04961223 },
    { ageDays: 4292, l: 0.4191715, m: 147.5525, s: 0.04969311 },
    { ageDays: 4322, l: 0.4163196, m: 148.0351, s: 0.0497687 },
    { ageDays: 4353, l: 0.4159232, m: 148.5447, s: 0.0498439 },
    { ageDays: 4383, l: 0.4184305, m: 149.0488, s: 0.04991365 },
    { ageDays: 4413, l: 0.4241858, m: 149.5642, s: 0.04998019 },
    { ageDays: 4444, l: 0.4339477, m: 150.1087, s: 0.0500452 },
    { ageDays: 4474, l: 0.4474435, m: 150.6477, s: 0.05010409 },
    { ageDays: 4505, l: 0.4659002, m: 151.217, s: 0.05016025 },
    { ageDays: 4535, l: 0.4883866, m: 151.7799, s: 0.05020946 },
    { ageDays: 4566, l: 0.5166063, m: 152.3738, s: 0.05025425 },
    { ageDays: 4596, l: 0.5488627, m: 152.9599, s: 0.05029097 },
    { ageDays: 4627, l: 0.5873543, m: 153.577, s: 0.05032114 },
    { ageDays: 4657, l: 0.6295621, m: 154.1845, s: 0.05034186 },
    { ageDays: 4687, l: 0.6765298, m: 154.8014, s: 0.05035328 },
    { ageDays: 4718, l: 0.7298558, m: 155.4479, s: 0.05035425 },
    { ageDays: 4748, l: 0.7858204, m: 156.081, s: 0.05034366 },
    { ageDays: 4779, l: 0.8477849, m: 156.7416, s: 0.05031976 },
    { ageDays: 4809, l: 0.9113361, m: 157.3859, s: 0.05028315 },
    { ageDays: 4840, l: 0.9802151, m: 158.0552, s: 0.05023048 },
    { ageDays: 4870, l: 1.049458, m: 158.7047, s: 0.05016443 },
    { ageDays: 4900, l: 1.1207, m: 159.3545, s: 0.05008296 },
    { ageDays: 4931, l: 1.195823, m: 160.0243, s: 0.04998214 },
    { ageDays: 4961, l: 1.269395, m: 160.6693, s: 0.04986826 },
    { ageDays: 4992, l: 1.345704, m: 161.3307, s: 0.04973368 },
    { ageDays: 5022, l: 1.419249, m: 161.9642, s: 0.04958729 },
    { ageDays: 5053, l: 1.494343, m: 162.6103, s: 0.04941972 },
    { ageDays: 5083, l: 1.56561, m: 163.2257, s: 0.04924238 },
    { ageDays: 5114, l: 1.637266, m: 163.85, s: 0.04904426 },
    { ageDays: 5144, l: 1.704209, m: 164.4415, s: 0.04883909 },
    { ageDays: 5174, l: 1.768365, m: 165.0194, s: 0.04862177 },
    { ageDays: 5205, l: 1.831324, m: 165.601, s: 0.04838571 },
    { ageDays: 5235, l: 1.888672, m: 166.148, s: 0.04814743 },
    { ageDays: 5266, l: 1.943911, m: 166.6959, s: 0.04789249 },
    { ageDays: 5296, l: 1.993214, m: 167.2087, s: 0.04763876 },
    { ageDays: 5327, l: 2.039643, m: 167.72, s: 0.04737083 },
    { ageDays: 5357, l: 2.080028, m: 168.1966, s: 0.0471074 },
    { ageDays: 5387, l: 2.115818, m: 168.6548, s: 0.04684128 },
    { ageDays: 5418, l: 2.147886, m: 169.109, s: 0.04656484 },
    { ageDays: 5448, l: 2.174122, m: 169.5299, s: 0.04629721 },
    { ageDays: 5479, l: 2.196275, m: 169.9455, s: 0.0460218 },
    { ageDays: 5509, l: 2.212956, m: 170.3293, s: 0.0457575 },
    { ageDays: 5540, l: 2.22536, m: 170.7072, s: 0.04548776 },
    { ageDays: 5570, l: 2.232798, m: 171.0551, s: 0.04523091 },
    { ageDays: 5601, l: 2.235921, m: 171.3966, s: 0.04497067 },
    { ageDays: 5631, l: 2.234704, m: 171.7103, s: 0.04472456 },
    { ageDays: 5661, l: 2.229518, m: 172.0078, s: 0.04448471 },
    { ageDays: 5692, l: 2.220223, m: 172.2989, s: 0.04424397 },
    { ageDays: 5722, l: 2.207665, m: 172.5654, s: 0.04401832 },
    { ageDays: 5753, l: 2.191285, m: 172.8257, s: 0.04379308 },
    { ageDays: 5783, l: 2.172414, m: 173.0636, s: 0.04358304 },
    { ageDays: 5814, l: 2.15009, m: 173.2955, s: 0.04337441 },
    { ageDays: 5844, l: 2.126046, m: 173.5072, s: 0.04318078 },
    { ageDays: 5874, l: 2.099888, m: 173.707, s: 0.04299535 },
    { ageDays: 5905, l: 2.070934, m: 173.9015, s: 0.04281236 },
    { ageDays: 5935, l: 2.041338, m: 174.0788, s: 0.04264358 },
    { ageDays: 5966, l: 2.009416, m: 174.2512, s: 0.04247769 },
    { ageDays: 5996, l: 1.977494, m: 174.4083, s: 0.04232528 },
    { ageDays: 6027, l: 1.94371, m: 174.5611, s: 0.04217602 },
    { ageDays: 6057, l: 1.910484, m: 174.7002, s: 0.04203938 },
    { ageDays: 6088, l: 1.875836, m: 174.8355, s: 0.04190606 },
    { ageDays: 6118, l: 1.84221, m: 174.9587, s: 0.04178443 },
    { ageDays: 6148, l: 1.808676, m: 175.0747, s: 0.04166986 },
    { ageDays: 6179, l: 1.774303, m: 175.1875, s: 0.04155863 },
    { ageDays: 6209, l: 1.741463, m: 175.2903, s: 0.04145769 },
    { ageDays: 6240, l: 1.708112, m: 175.3903, s: 0.04136003 },
    { ageDays: 6270, l: 1.676523, m: 175.4814, s: 0.0412717 },
    { ageDays: 6301, l: 1.6447, m: 175.57, s: 0.04118655 },
    { ageDays: 6331, l: 1.614785, m: 175.6508, s: 0.04110981 },
    { ageDays: 6361, l: 1.58581, m: 175.7271, s: 0.04103839 },
    { ageDays: 6392, l: 1.556919, m: 175.8014, s: 0.04096992 },
    { ageDays: 6422, l: 1.530022, m: 175.8692, s: 0.04090856 },
    { ageDays: 6453, l: 1.503364, m: 175.9353, s: 0.04084998 },
    { ageDays: 6483, l: 1.478689, m: 175.9957, s: 0.0407977 },
    { ageDays: 6514, l: 1.454367, m: 176.0546, s: 0.04074801 },
    { ageDays: 6544, l: 1.431972, m: 176.1084, s: 0.04070389 },
    { ageDays: 6575, l: 1.41001, m: 176.1611, s: 0.04066215 },
    { ageDays: 6605, l: 1.389888, m: 176.2092, s: 0.04062528 },
    { ageDays: 6635, l: 1.370864, m: 176.2548, s: 0.04059169 },
    { ageDays: 6666, l: 1.352339, m: 176.2994, s: 0.0405602 },
    { ageDays: 6696, l: 1.335484, m: 176.3403, s: 0.04053266 },
    { ageDays: 6727, l: 1.319144, m: 176.3804, s: 0.04050705 },
    { ageDays: 6757, l: 1.304345, m: 176.4171, s: 0.04048485 },
    { ageDays: 6788, l: 1.290062, m: 176.4531, s: 0.0404644 },
    { ageDays: 6818, l: 1.277185, m: 176.4862, s: 0.04044687 },
    { ageDays: 6848, l: 1.265198, m: 176.5177, s: 0.0404314 },
    { ageDays: 6879, l: 1.25371, m: 176.5486, s: 0.04041745 },
    { ageDays: 6909, l: 1.243424, m: 176.577, s: 0.04040578 },
    { ageDays: 6940, l: 1.233614, m: 176.6049, s: 0.04039548 },
    { ageDays: 6970, l: 1.224875, m: 176.6306, s: 0.04038708 },
    { ageDays: 7001, l: 1.216584, m: 176.6559, s: 0.04037992 },
    { ageDays: 7031, l: 1.20924, m: 176.6792, s: 0.04037433 },
    { ageDays: 7062, l: 1.202314, m: 176.7022, s: 0.04036985 },
    { ageDays: 7092, l: 1.196219, m: 176.7234, s: 0.04036666 },
    { ageDays: 7122, l: 1.190685, m: 176.7436, s: 0.04036452 },
    { ageDays: 7153, l: 1.185523, m: 176.7636, s: 0.04036332 },
    { ageDays: 7183, l: 1.181033, m: 176.7821, s: 0.04036305 },
    { ageDays: 7214, l: 1.176883, m: 176.8003, s: 0.04036362 },
    { ageDays: 7244, l: 1.173311, m: 176.8171, s: 0.04036492 },
    { ageDays: 7275, l: 1.170049, m: 176.8338, s: 0.04036697 },
    { ageDays: 7305, l: 1.167279, m: 176.8492, s: 0.04036957 },
  ],
  female: [
    { ageDays: 731, l: 1.071753, m: 84.98941, s: 0.04079364 },
    { ageDays: 761, l: 1.044298, m: 85.84199, s: 0.04099073 },
    { ageDays: 791, l: 1.029681, m: 86.7164, s: 0.04125145 },
    { ageDays: 822, l: 0.9925597, m: 87.58534, s: 0.04143191 },
    { ageDays: 852, l: 0.9469216, m: 88.39784, s: 0.04155893 },
    { ageDays: 883, l: 0.8943978, m: 89.20694, s: 0.04165497 },
    { ageDays: 913, l: 0.8415834, m: 89.95984, s: 0.04172443 },
    { ageDays: 944, l: 0.7872835, m: 90.70659, s: 0.04178049 },
    { ageDays: 974, l: 0.736481, m: 91.39929, s: 0.04182579 },
    { ageDays: 1004, l: 0.6882815, m: 92.06319, s: 0.04186682 },
    { ageDays: 1035, l: 0.6417104, m: 92.72, s: 0.04190813 },
    { ageDays: 1065, l: 0.599935, m: 93.32864, s: 0.04194928 },
    { ageDays: 1096, l: 0.5598227, m: 93.92966, s: 0.04199212 },
    { ageDays: 1126, l: 0.5263333, m: 94.50143, s: 0.04205741 },
    { ageDays: 1157, l: 0.4965581, m: 95.09292, s: 0.04215232 },
    { ageDays: 1187, l: 0.4690753, m: 95.65974, s: 0.0422491 },
    { ageDays: 1218, l: 0.4418103, m: 96.24143, s: 0.04235409 },
    { ageDays: 1248, l: 0.416142, m: 96.80135, s: 0.04245894 },
    { ageDays: 1278, l: 0.390899, m: 97.35915, s: 0.04256568 },
    { ageDays: 1309, l: 0.3650494, m: 97.93405, s: 0.04267686 },
    { ageDays: 1339, l: 0.3401203, m: 98.4896, s: 0.04278442 },
    { ageDays: 1370, l: 0.3143584, m: 99.06342, s: 0.0428948 },
    { ageDays: 1400, l: 0.2893808, m: 99.61896, s: 0.0430003 },
    { ageDays: 1431, l: 0.2635107, m: 100.1937, s: 0.04310752 },
    { ageDays: 1461, l: 0.2384328, m: 100.7508, s: 0.04320919 },
    { ageDays: 1491, l: 0.2133485, m: 101.3092, s: 0.04330857 },
    { ageDays: 1522, l: 0.1874744, m: 101.8877, s: 0.04340867 },
    { ageDays: 1552, l: 0.1625428, m: 102.4492, s: 0.0435029 },
    { ageDays: 1583, l: 0.1369656, m: 103.0313, s: 0.04359747 },
    { ageDays: 1613, l: 0.1124693, m: 103.5964, s: 0.04368625 },
    { ageDays: 1644, l: 0.08750345, m: 104.1822, s: 0.04377515 },
    { ageDays: 1674, l: 0.06375939, m: 104.7511, s: 0.04385845 },
    { ageDays: 1705, l: 0.03973796, m: 105.3407, s: 0.04394176 },
    { ageDays: 1735, l: 0.01706766, m: 105.9131, s: 0.04401974 },
    { ageDays: 1765, l: -0.004960802, m: 106.4871, s: 0.04409519 },
    { ageDays: 1796, l: -0.02697379, m: 107.0818, s: 0.04417058 },
    { ageDays: 1826, l: -0.04748192, m: 107.6587, s: 0.04424112 },
    { ageDays: 1857, l: -0.06778467, m: 108.256, s: 0.04431159 },
    { ageDays: 1887, l: -0.0865121, m: 108.8351, s: 0.04437754 },
    { ageDays: 1918, l: -0.1048552, m: 109.4342, s: 0.04444344 },
    { ageDays: 1948, l: -0.1215808, m: 110.0146, s: 0.04450513 },
    { ageDays: 1978, l: -0.1372537, m: 110.5953, s: 0.04456484 },
    { ageDays: 2009, l: -0.1523029, m: 111.1954, s: 0.04462457 },
    { ageDays: 2039, l: -0.1657241, m: 111.776, s: 0.04468052 },
    { ageDays: 2070, l: -0.1783829, m: 112.3754, s: 0.04473653 },
    { ageDays: 2100, l: -0.1894424, m: 112.9548, s: 0.04478905 },
    { ageDays: 2131, l: -0.199615, m: 113.5523, s: 0.04484166 },
    { ageDays: 2161, l: -0.2082405, m: 114.1293, s: 0.04489106 },
    { ageDays: 2192, l: -0.2158905, m: 114.7239, s: 0.04494061 },
    { ageDays: 2222, l: -0.222062, m: 115.2974, s: 0.04498721 },
    { ageDays: 2252, l: -0.2270304, m: 115.8687, s: 0.04503253 },
    { ageDays: 2283, l: -0.2309095, m: 116.4566, s: 0.04507814 },
    { ageDays: 2313, l: -0.2334632, m: 117.0228, s: 0.04512115 },
    { ageDays: 2344, l: -0.2348811, m: 117.6047, s: 0.04516453 },
    { ageDays: 2374, l: -0.2350947, m: 118.1646, s: 0.04520558 },
    { ageDays: 2405, l: -0.2341467, m: 118.7395, s: 0.04524713 },
    { ageDays: 2435, l: -0.2321295, m: 119.2921, s: 0.0452866 },
    { ageDays: 2465, l: -0.229065, m: 119.8408, s: 0.04532545 },
    { ageDays: 2496, l: -0.2248381, m: 120.4033, s: 0.04536507 },
    { ageDays: 2526, l: -0.2197632, m: 120.9433, s: 0.04540301 },
    { ageDays: 2557, l: -0.2135492, m: 121.4964, s: 0.04544195 },
    { ageDays: 2587, l: -0.2066449, m: 122.0269, s: 0.04547951 },
    { ageDays: 2618, l: -0.1986427, m: 122.5698, s: 0.04551835 },
    { ageDays: 2648, l: -0.1901118, m: 123.0901, s: 0.04555613 },
    { ageDays: 2679, l: -0.1805406, m: 123.6222, s: 0.04559553 },
    { ageDays: 2709, l: -0.1706032, m: 124.1317, s: 0.04563421 },
    { ageDays: 2739, l: -0.1600588, m: 124.6358, s: 0.04567362 },
    { ageDays: 2770, l: -0.1485862, m: 125.1509, s: 0.04571531 },
    { ageDays: 2800, l: -0.136985, m: 125.6437, s: 0.04575682 },
    { ageDays: 2831, l: -0.124544, m: 126.147, s: 0.04580118 },
    { ageDays: 2861, l: -0.1121252, m: 126.6284, s: 0.04584577 },
    { ageDays: 2892, l: -0.09896188, m: 127.1199, s: 0.04589382 },
    { ageDays: 2922, l: -0.08596022, m: 127.59, s: 0.04594253 },
    { ageDays: 2952, l: -0.07275406, m: 128.0545, s: 0.04599371 },
    { ageDays: 2983, l: -0.05894683, m: 128.5288, s: 0.04604949 },
    { ageDays: 3013, l: -0.04547803, m: 128.9824, s: 0.0461066 },
    { ageDays: 3044, l: -0.03149602, m: 129.4457, s: 0.0461692 },
    { ageDays: 3074, l: -0.01794183, m: 129.889, s: 0.0462336 },
    { ageDays: 3105, l: -0.003945879, m: 130.3422, s: 0.04630445 },
    { ageDays: 3135, l: 0.009563734, m: 130.7761, s: 0.04637756 },
    { ageDays: 3166, l: 0.02347044, m: 131.22, s: 0.04645817 },
    { ageDays: 3196, l: 0.03687044, m: 131.6456, s: 0.04654145 },
    { ageDays: 3226, l: 0.05021768, m: 132.0677, s: 0.04663027 },
    { ageDays: 3257, l: 0.06397189, m: 132.5006, s: 0.04672823 },
    { ageDays: 3287, l: 0.07727629, m: 132.9167, s: 0.04682935 },
    { ageDays: 3318, l: 0.09106545, m: 133.3443, s: 0.04694068 },
    { ageDays: 3348, l: 0.1045115, m: 133.7564, s: 0.04705531 },
    { ageDays: 3379, l: 0.1185931, m: 134.181, s: 0.04718112 },
    { ageDays: 3409, l: 0.1324986, m: 134.5914, s: 0.04731017 },
    { ageDays: 3439, l: 0.1467905, m: 135.0018, s: 0.04744649 },
    { ageDays: 3470, l: 0.1621024, m: 135.4268, s: 0.04759499 },
    { ageDays: 3500, l: 0.1775971, m: 135.8395, s: 0.04774601 },
    { ageDays: 3531, l: 0.1944823, m: 136.2684, s: 0.04790937 },
    { ageDays: 3561, l: 0.2118508, m: 136.6865, s: 0.04807419 },
    { ageDays: 3592, l: 0.2310632, m: 137.1227, s: 0.04825088 },
    { ageDays: 3622, l: 0.2510827, m: 137.5496, s: 0.04842735 },
    { ageDays: 3653, l: 0.2734599, m: 137.9966, s: 0.04861438 },
    { ageDays: 3683, l: 0.2969565, m: 138.4357, s: 0.04879877 },
    { ageDays: 3713, l: 0.3224587, m: 138.882, s: 0.04898512 },
    { ageDays: 3744, l: 0.3511072, m: 139.3518, s: 0.04917805 },
    { ageDays: 3774, l: 0.3812156, m: 139.8153, s: 0.0493632 },
    { ageDays: 3805, l: 0.4149259, m: 140.3045, s: 0.04955063 },
    { ageDays: 3835, l: 0.4501551, m: 140.7882, s: 0.04972582 },
    { ageDays: 3866, l: 0.4893, m: 141.2993, s: 0.04989762 },
    { ageDays: 3896, l: 0.5298244, m: 141.8053, s: 0.05005207 },
    { ageDays: 3926, l: 0.5728781, m: 142.3228, s: 0.05019188 },
    { ageDays: 3957, l: 0.6198812, m: 142.8696, s: 0.05031767 },
    { ageDays: 3987, l: 0.6675925, m: 143.4105, s: 0.0504181 },
    { ageDays: 4018, l: 0.7188978, m: 143.9812, s: 0.05049654 },
    { ageDays: 4048, l: 0.7701312, m: 144.5444, s: 0.05054487 },
    { ageDays: 4079, l: 0.8242618, m: 145.1369, s: 0.05056334 },
    { ageDays: 4109, l: 0.8773035, m: 145.7194, s: 0.05054827 },
    { ageDays: 4140, l: 0.9322143, m: 146.3295, s: 0.05049654 },
    { ageDays: 4170, l: 0.9848503, m: 146.9265, s: 0.05040995 },
    { ageDays: 4200, l: 1.03636, m: 147.5283, s: 0.05028657 },
    { ageDays: 4231, l: 1.087717, m: 148.1533, s: 0.05012019 },
    { ageDays: 4261, l: 1.134945, m: 148.7593, s: 0.04992203 },
    { ageDays: 4292, l: 1.180509, m: 149.3844, s: 0.04968016 },
    { ageDays: 4322, l: 1.22084, m: 149.9862, s: 0.04941211 },
    { ageDays: 4353, l: 1.25803, m: 150.6025, s: 0.04910269 },
    { ageDays: 4383, l: 1.289187, m: 151.1913, s: 0.04877501 },
    { ageDays: 4413, l: 1.315186, m: 151.7707, s: 0.04842316 },
    { ageDays: 4444, l: 1.336321, m: 152.3574, s: 0.04803831 },
    { ageDays: 4474, l: 1.351047, m: 152.9117, s: 0.04764953 },
    { ageDays: 4505, l: 1.36029, m: 153.4689, s: 0.04723548 },
    { ageDays: 4535, l: 1.363534, m: 153.9914, s: 0.04682722 },
    { ageDays: 4566, l: 1.361214, m: 154.5128, s: 0.04640196 },
    { ageDays: 4596, l: 1.353811, m: 154.9985, s: 0.04599115 },
    { ageDays: 4627, l: 1.34129, m: 155.48, s: 0.04557128 },
    { ageDays: 4657, l: 1.32499, m: 155.9257, s: 0.04517277 },
    { ageDays: 4687, l: 1.30516, m: 156.3512, s: 0.04478482 },
    { ageDays: 4718, l: 1.281623, m: 156.7695, s: 0.0443976 },
    { ageDays: 4748, l: 1.256559, m: 157.1535, s: 0.04403811 },
    { ageDays: 4779, l: 1.228982, m: 157.5293, s: 0.04368406 },
    { ageDays: 4809, l: 1.201302, m: 157.8729, s: 0.04335943 },
    { ageDays: 4840, l: 1.172288, m: 158.2077, s: 0.04304339 },
    { ageDays: 4870, l: 1.144344, m: 158.5127, s: 0.04275674 },
    { ageDays: 4900, l: 1.116991, m: 158.7998, s: 0.04248904 },
    { ageDays: 4931, l: 1.089752, m: 159.0783, s: 0.04223215 },
    { ageDays: 4961, l: 1.064706, m: 159.331, s: 0.04200224 },
    { ageDays: 4992, l: 1.040443, m: 159.5757, s: 0.04178336 },
    { ageDays: 5022, l: 1.018709, m: 159.7974, s: 0.04158889 },
    { ageDays: 5053, l: 0.9981786, m: 160.0118, s: 0.04140498 },
    { ageDays: 5083, l: 0.9802401, m: 160.2057, s: 0.04124257 },
    { ageDays: 5114, l: 0.9637111, m: 160.3931, s: 0.04108985 },
    { ageDays: 5144, l: 0.949643, m: 160.5626, s: 0.04095566 },
    { ageDays: 5174, l: 0.9374198, m: 160.7212, s: 0.04083389 },
    { ageDays: 5205, l: 0.9266385, m: 160.8743, s: 0.04072012 },
    { ageDays: 5235, l: 0.9179007, m: 161.0129, s: 0.04062071 },
    { ageDays: 5266, l: 0.9105119, m: 161.1467, s: 0.04052813 },
    { ageDays: 5296, l: 0.9048341, m: 161.2678, s: 0.04044747 },
    { ageDays: 5327, l: 0.9003649, m: 161.3849, s: 0.04037251 },
    { ageDays: 5357, l: 0.8972792, m: 161.491, s: 0.04030733 },
    { ageDays: 5387, l: 0.8952958, m: 161.5905, s: 0.0402487 },
    { ageDays: 5418, l: 0.8942834, m: 161.6868, s: 0.04019432 },
    { ageDays: 5448, l: 0.8942028, m: 161.7742, s: 0.0401471 },
    { ageDays: 5479, l: 0.8949427, m: 161.859, s: 0.04010332 },
    { ageDays: 5509, l: 0.8963612, m: 161.936, s: 0.0400653 },
    { ageDays: 5540, l: 0.8984621, m: 162.0108, s: 0.04003004 },
    { ageDays: 5570, l: 0.9010297, m: 162.0789, s: 0.03999939 },
    { ageDays: 5601, l: 0.904159, m: 162.1451, s: 0.03997093 },
    { ageDays: 5631, l: 0.9075821, m: 162.2055, s: 0.03994616 },
    { ageDays: 5661, l: 0.9113346, m: 162.2625, s: 0.03992381 },
    { ageDays: 5692, l: 0.9155027, m: 162.318, s: 0.03990299 },
    { ageDays: 5722, l: 0.9197694, m: 162.3689, s: 0.03988479 },
    { ageDays: 5753, l: 0.9243744, m: 162.4185, s: 0.03986778 },
    { ageDays: 5783, l: 0.9289826, m: 162.4639, s: 0.03985285 },
    { ageDays: 5814, l: 0.9338657, m: 162.5084, s: 0.03983885 },
    { ageDays: 5844, l: 0.9386793, m: 162.5491, s: 0.03982651 },
    { ageDays: 5874, l: 0.9435536, m: 162.5878, s: 0.03981523 },
    { ageDays: 5905, l: 0.9486303, m: 162.6258, s: 0.03980457 },
    { ageDays: 5935, l: 0.9535619, m: 162.6607, s: 0.03979509 },
    { ageDays: 5966, l: 0.958659, m: 162.695, s: 0.03978608 },
    { ageDays: 5996, l: 0.9635775, m: 162.7265, s: 0.03977804 },
    { ageDays: 6027, l: 0.9686317, m: 162.7575, s: 0.03977033 },
    { ageDays: 6057, l: 0.9734843, m: 162.7862, s: 0.03976342 },
    { ageDays: 6088, l: 0.9784486, m: 162.8144, s: 0.03975675 },
    { ageDays: 6118, l: 0.9831963, m: 162.8404, s: 0.03975072 },
    { ageDays: 6148, l: 0.9878816, m: 162.8653, s: 0.03974506 },
    { ageDays: 6179, l: 0.9926515, m: 162.8898, s: 0.03973956 },
    { ageDays: 6209, l: 0.9971933, m: 162.9125, s: 0.03973453 },
    { ageDays: 6240, l: 1.001806, m: 162.9349, s: 0.03972962 },
    { ageDays: 6270, l: 1.006188, m: 162.9557, s: 0.03972511 },
    { ageDays: 6301, l: 1.01063, m: 162.9763, s: 0.03972068 },
    { ageDays: 6331, l: 1.014844, m: 162.9953, s: 0.03971659 },
    { ageDays: 6361, l: 1.018971, m: 163.0136, s: 0.03971268 },
    { ageDays: 6392, l: 1.023144, m: 163.0317, s: 0.0397088 },
    { ageDays: 6422, l: 1.027094, m: 163.0485, s: 0.0397052 },
    { ageDays: 6453, l: 1.031084, m: 163.0651, s: 0.03970163 },
    { ageDays: 6483, l: 1.034856, m: 163.0805, s: 0.03969829 },
    { ageDays: 6514, l: 1.038662, m: 163.0959, s: 0.03969496 },
    { ageDays: 6544, l: 1.042258, m: 163.1101, s: 0.03969186 },
    { ageDays: 6575, l: 1.045882, m: 163.1243, s: 0.03968875 },
    { ageDays: 6605, l: 1.049303, m: 163.1374, s: 0.03968584 },
    { ageDays: 6635, l: 1.05264, m: 163.1501, s: 0.03968302 },
    { ageDays: 6666, l: 1.056001, m: 163.1626, s: 0.03968018 },
    { ageDays: 6696, l: 1.059169, m: 163.1744, s: 0.03967752 },
    { ageDays: 6727, l: 1.062359, m: 163.186, s: 0.03967485 },
    { ageDays: 6757, l: 1.065365, m: 163.1968, s: 0.03967234 },
    { ageDays: 6788, l: 1.06839, m: 163.2076, s: 0.03966981 },
    { ageDays: 6818, l: 1.071239, m: 163.2176, s: 0.03966744 },
    { ageDays: 6848, l: 1.074013, m: 163.2273, s: 0.03966512 },
    { ageDays: 6879, l: 1.076802, m: 163.2369, s: 0.03966279 },
    { ageDays: 6909, l: 1.079428, m: 163.2459, s: 0.03966059 },
    { ageDays: 6940, l: 1.082067, m: 163.2548, s: 0.03965839 },
    { ageDays: 6970, l: 1.084551, m: 163.2631, s: 0.0396563 },
    { ageDays: 7001, l: 1.087047, m: 163.2714, s: 0.03965421 },
    { ageDays: 7031, l: 1.089396, m: 163.2792, s: 0.03965222 },
    { ageDays: 7062, l: 1.091755, m: 163.2868, s: 0.03965023 },
    { ageDays: 7092, l: 1.093974, m: 163.294, s: 0.03964835 },
    { ageDays: 7122, l: 1.096131, m: 163.301, s: 0.03964652 },
    { ageDays: 7153, l: 1.098298, m: 163.3079, s: 0.03964468 },
    { ageDays: 7183, l: 1.100335, m: 163.3143, s: 0.03964294 },
    { ageDays: 7214, l: 1.10238, m: 163.3207, s: 0.03964119 },
    { ageDays: 7244, l: 1.104303, m: 163.3267, s: 0.03963954 },
    { ageDays: 7275, l: 1.106232, m: 163.3327, s: 0.03963788 },
    { ageDays: 7305, l: 1.108046, m: 163.3383, s: 0.03963632 },
  ],
};

/** Head-circumference-for-age, 24 through 36 months (CDC does not publish this chart past 36 months) — monthly LMS values, plus the exact last real data day (1095) rather than forcing it onto the monthly grid. */
export const CDC_HEAD_CIRCUMFERENCE_CM_REFERENCE: Readonly<
  Record<'male' | 'female', readonly CdcReferenceRow[]>
> = {
  male: [
    { ageDays: 731, l: 1.352843, m: 48.66334, s: 0.02911841 },
    { ageDays: 761, l: 1.497818, m: 48.77829, s: 0.02937329 },
    { ageDays: 791, l: 1.641491, m: 48.88624, s: 0.0296376 },
    { ageDays: 822, l: 1.787751, m: 48.99099, s: 0.02991939 },
    { ageDays: 852, l: 1.926505, m: 49.08628, s: 0.03019945 },
    { ageDays: 883, l: 2.066469, m: 49.17891, s: 0.03049549 },
    { ageDays: 913, l: 2.198224, m: 49.2633, s: 0.03078764 },
    { ageDays: 944, l: 2.330247, m: 49.34546, s: 0.03109465 },
    { ageDays: 974, l: 2.453816, m: 49.42039, s: 0.03139613 },
    { ageDays: 1004, l: 2.57312, m: 49.49112, s: 0.03170137 },
    { ageDays: 1035, l: 2.691837, m: 49.56006, s: 0.03202029 },
    { ageDays: 1065, l: 2.802274, m: 49.62302, s: 0.03233189 },
    { ageDays: 1095, l: 2.90831, m: 49.68247, s: 0.03264606 },
  ],
  female: [
    { ageDays: 731, l: 1.670309, m: 47.47414, s: 0.02939107 },
    { ageDays: 761, l: 1.753032, m: 47.60017, s: 0.02957212 },
    { ageDays: 791, l: 1.82984, m: 47.71929, s: 0.02976045 },
    { ageDays: 822, l: 1.903386, m: 47.83567, s: 0.02996189 },
    { ageDays: 852, l: 1.96929, m: 47.94227, s: 0.03016274 },
    { ageDays: 883, l: 2.032307, m: 48.04665, s: 0.03037575 },
    { ageDays: 913, l: 2.088697, m: 48.14247, s: 0.03058664 },
    { ageDays: 944, l: 2.142541, m: 48.23647, s: 0.03080896 },
    { ageDays: 974, l: 2.190656, m: 48.32291, s: 0.03102795 },
    { ageDays: 1004, l: 2.235112, m: 48.40518, s: 0.03125035 },
    { ageDays: 1035, l: 2.277467, m: 48.48607, s: 0.03148338 },
    { ageDays: 1065, l: 2.31522, m: 48.56061, s: 0.03171172 },
    { ageDays: 1095, l: 2.349997, m: 48.63167, s: 0.03194259 },
  ],
};

/** BMI-for-age, 24 months through 20 years, monthly LMS values. */
export const CDC_BMI_REFERENCE: Readonly<Record<'male' | 'female', readonly CdcReferenceRow[]>> = {
  male: [
    { ageDays: 731, l: -2.010235, m: 16.57413, s: 0.08057719 },
    { ageDays: 761, l: -1.953199, m: 16.5208, s: 0.07967277 },
    { ageDays: 791, l: -1.895519, m: 16.46896, s: 0.07881599 },
    { ageDays: 822, l: -1.83593, m: 16.41693, s: 0.07798042 },
    { ageDays: 852, l: -1.778977, m: 16.36801, s: 0.07722002 },
    { ageDays: 883, l: -1.721622, m: 16.31893, s: 0.07648411 },
    { ageDays: 913, l: -1.668307, m: 16.2728, s: 0.07582026 },
    { ageDays: 944, l: -1.616253, m: 16.22653, s: 0.0751843 },
    { ageDays: 974, l: -1.569542, m: 16.18306, s: 0.07461736 },
    { ageDays: 1004, l: -1.527121, m: 16.14086, s: 0.07409815 },
    { ageDays: 1035, l: -1.488479, m: 16.09856, s: 0.07361177 },
    { ageDays: 1065, l: -1.456718, m: 16.05885, s: 0.0731895 },
    { ageDays: 1096, l: -1.430285, m: 16.01906, s: 0.07280301 },
    { ageDays: 1126, l: -1.411342, m: 15.98175, s: 0.07247692 },
    { ageDays: 1157, l: -1.399043, m: 15.94441, s: 0.07218903 },
    { ageDays: 1187, l: -1.394454, m: 15.90942, s: 0.07195731 },
    { ageDays: 1218, l: -1.397455, m: 15.87444, s: 0.0717656 },
    { ageDays: 1248, l: -1.407958, m: 15.84171, s: 0.07162534 },
    { ageDays: 1278, l: -1.425919, m: 15.81007, s: 0.07152859 },
    { ageDays: 1309, l: -1.452184, m: 15.77852, s: 0.07147313 },
    { ageDays: 1339, l: -1.484842, m: 15.74909, s: 0.07146121 },
    { ageDays: 1370, l: -1.52578, m: 15.71981, s: 0.07149053 },
    { ageDays: 1400, l: -1.571948, m: 15.69256, s: 0.07155769 },
    { ageDays: 1431, l: -1.625919, m: 15.66554, s: 0.07166551 },
    { ageDays: 1461, l: -1.683678, m: 15.64047, s: 0.0718054 },
    { ageDays: 1491, l: -1.746284, m: 15.61649, s: 0.07197863 },
    { ageDays: 1522, l: -1.815403, m: 15.59285, s: 0.07219096 },
    { ageDays: 1552, l: -1.885893, m: 15.57109, s: 0.07242707 },
    { ageDays: 1583, l: -1.961733, m: 15.54975, s: 0.07270107 },
    { ageDays: 1613, l: -2.037301, m: 15.53024, s: 0.07299385 },
    { ageDays: 1644, l: -2.116897, m: 15.51125, s: 0.07332348 },
    { ageDays: 1674, l: -2.194716, m: 15.49403, s: 0.07366741 },
    { ageDays: 1705, l: -2.275241, m: 15.47743, s: 0.07404739 },
    { ageDays: 1735, l: -2.352651, m: 15.46254, s: 0.07443787 },
    { ageDays: 1765, l: -2.428969, m: 15.44881, s: 0.07484987 },
    { ageDays: 1796, l: -2.506134, m: 15.43586, s: 0.07529738 },
    { ageDays: 1826, l: -2.578699, m: 15.42452, s: 0.07575081 },
    { ageDays: 1857, l: -2.651058, m: 15.41406, s: 0.07623978 },
    { ageDays: 1887, l: -2.718185, m: 15.40513, s: 0.07673221 },
    { ageDays: 1918, l: -2.784231, m: 15.39717, s: 0.07726046 },
    { ageDays: 1948, l: -2.8447, m: 15.39067, s: 0.07779 },
    { ageDays: 1978, l: -2.901606, m: 15.38537, s: 0.07833716 },
    { ageDays: 2009, l: -2.956518, m: 15.38113, s: 0.0789207 },
    { ageDays: 2039, l: -3.005812, m: 15.37824, s: 0.07950258 },
    { ageDays: 2070, l: -3.052732, m: 15.37649, s: 0.0801212 },
    { ageDays: 2100, l: -3.094249, m: 15.37598, s: 0.08073624 },
    { ageDays: 2131, l: -3.133168, m: 15.37668, s: 0.08138825 },
    { ageDays: 2161, l: -3.167043, m: 15.37853, s: 0.08203473 },
    { ageDays: 2192, l: -3.198225, m: 15.38163, s: 0.08271827 },
    { ageDays: 2222, l: -3.224816, m: 15.38579, s: 0.08339426 },
    { ageDays: 2252, l: -3.248005, m: 15.39105, s: 0.08408396 },
    { ageDays: 2283, l: -3.268535, m: 15.39766, s: 0.08481049 },
    { ageDays: 2313, l: -3.285236, m: 15.40515, s: 0.08552637 },
    { ageDays: 2344, l: -3.299391, m: 15.41402, s: 0.08627862 },
    { ageDays: 2374, l: -3.310246, m: 15.42368, s: 0.08701807 },
    { ageDays: 2405, l: -3.318696, m: 15.43475, s: 0.08779332 },
    { ageDays: 2435, l: -3.324363, m: 15.44652, s: 0.08855364 },
    { ageDays: 2465, l: -3.327719, m: 15.4593, s: 0.08932317 },
    { ageDays: 2496, l: -3.328922, m: 15.47355, s: 0.0901273 },
    { ageDays: 2526, l: -3.328054, m: 15.48834, s: 0.09091338 },
    { ageDays: 2557, l: -3.325218, m: 15.50464, s: 0.09173306 },
    { ageDays: 2587, l: -3.320742, m: 15.52138, s: 0.09253269 },
    { ageDays: 2618, l: -3.314475, m: 15.53966, s: 0.09336481 },
    { ageDays: 2648, l: -3.306953, m: 15.55828, s: 0.09417497 },
    { ageDays: 2679, l: -3.29781, m: 15.57849, s: 0.09501643 },
    { ageDays: 2709, l: -3.287756, m: 15.59894, s: 0.09583414 },
    { ageDays: 2739, l: -3.276628, m: 15.62027, s: 0.09665448 },
    { ageDays: 2770, l: -3.264112, m: 15.64323, s: 0.09750418 },
    { ageDays: 2800, l: -3.251117, m: 15.66631, s: 0.09832771 },
    { ageDays: 2831, l: -3.236876, m: 15.69103, s: 0.09917924 },
    { ageDays: 2861, l: -3.222396, m: 15.71579, s: 0.1000031 },
    { ageDays: 2892, l: -3.206799, m: 15.74223, s: 0.1008536 },
    { ageDays: 2922, l: -3.191167, m: 15.76862, s: 0.1016752 },
    { ageDays: 2952, l: -3.175078, m: 15.7958, s: 0.1024946 },
    { ageDays: 2983, l: -3.158039, m: 15.82469, s: 0.1033385 },
    { ageDays: 3013, l: -3.141212, m: 15.85342, s: 0.1041518 },
    { ageDays: 3044, l: -3.123536, m: 15.88388, s: 0.1049881 },
    { ageDays: 3074, l: -3.1062, m: 15.91411, s: 0.1057929 },
    { ageDays: 3105, l: -3.088102, m: 15.94611, s: 0.1066192 },
    { ageDays: 3135, l: -3.070453, m: 15.97779, s: 0.1074132 },
    { ageDays: 3166, l: -3.052116, m: 16.01126, s: 0.1082273 },
    { ageDays: 3196, l: -3.034314, m: 16.04434, s: 0.1090084 },
    { ageDays: 3226, l: -3.01649, m: 16.0781, s: 0.1097825 },
    { ageDays: 3257, l: -2.99808, m: 16.11368, s: 0.1105745 },
    { ageDays: 3287, l: -2.980297, m: 16.14878, s: 0.1113329 },
    { ageDays: 3318, l: -2.961983, m: 16.18571, s: 0.1121078 },
    { ageDays: 3348, l: -2.944346, m: 16.22209, s: 0.1128487 },
    { ageDays: 3379, l: -2.926229, m: 16.26034, s: 0.1136048 },
    { ageDays: 3409, l: -2.90882, m: 16.29797, s: 0.1143268 },
    { ageDays: 3439, l: -2.891548, m: 16.33619, s: 0.1150389 },
    { ageDays: 3470, l: -2.87386, m: 16.3763, s: 0.1157641 },
    { ageDays: 3500, l: -2.856911, m: 16.41571, s: 0.1164551 },
    { ageDays: 3531, l: -2.839582, m: 16.45702, s: 0.1171579 },
    { ageDays: 3561, l: -2.823001, m: 16.49756, s: 0.1178267 },
    { ageDays: 3592, l: -2.806073, m: 16.54003, s: 0.118506 },
    { ageDays: 3622, l: -2.789897, m: 16.58167, s: 0.1191515 },
    { ageDays: 3653, l: -2.773401, m: 16.62525, s: 0.1198061 },
    { ageDays: 3683, l: -2.757654, m: 16.66795, s: 0.1204274 },
    { ageDays: 3713, l: -2.742126, m: 16.71115, s: 0.1210365 },
    { ageDays: 3744, l: -2.726314, m: 16.75632, s: 0.1216528 },
    { ageDays: 3774, l: -2.71124, m: 16.80052, s: 0.1222365 },
    { ageDays: 3805, l: -2.695901, m: 16.84669, s: 0.1228263 },
    { ageDays: 3835, l: -2.681289, m: 16.89185, s: 0.123384 },
    { ageDays: 3866, l: -2.666429, m: 16.93899, s: 0.1239466 },
    { ageDays: 3896, l: -2.652281, m: 16.98507, s: 0.1244778 },
    { ageDays: 3926, l: -2.63836, m: 17.03159, s: 0.1249958 },
    { ageDays: 3957, l: -2.624214, m: 17.08011, s: 0.1255172 },
    { ageDays: 3987, l: -2.610753, m: 17.12749, s: 0.1260082 },
    { ageDays: 4018, l: -2.597079, m: 17.17688, s: 0.1265015 },
    { ageDays: 4048, l: -2.58407, m: 17.22509, s: 0.1269653 },
    { ageDays: 4079, l: -2.570857, m: 17.27532, s: 0.1274304 },
    { ageDays: 4109, l: -2.55829, m: 17.32431, s: 0.1278669 },
    { ageDays: 4140, l: -2.545524, m: 17.37534, s: 0.1283038 },
    { ageDays: 4170, l: -2.533381, m: 17.42509, s: 0.1287129 },
    { ageDays: 4200, l: -2.521445, m: 17.4752, s: 0.1291086 },
    { ageDays: 4231, l: -2.509322, m: 17.52734, s: 0.1295034 },
    { ageDays: 4261, l: -2.49779, m: 17.57815, s: 0.129872 },
    { ageDays: 4292, l: -2.486075, m: 17.631, s: 0.1302388 },
    { ageDays: 4322, l: -2.47493, m: 17.68247, s: 0.1305803 },
    { ageDays: 4353, l: -2.463605, m: 17.73599, s: 0.1309194 },
    { ageDays: 4383, l: -2.452827, m: 17.78809, s: 0.1312343 },
    { ageDays: 4413, l: -2.442223, m: 17.84049, s: 0.1315362 },
    { ageDays: 4444, l: -2.431442, m: 17.89493, s: 0.1318346 },
    { ageDays: 4474, l: -2.421174, m: 17.94791, s: 0.1321103 },
    { ageDays: 4505, l: -2.410731, m: 18.00294, s: 0.132382 },
    { ageDays: 4535, l: -2.400781, m: 18.05646, s: 0.1326322 },
    { ageDays: 4566, l: -2.390653, m: 18.11202, s: 0.1328777 },
    { ageDays: 4596, l: -2.380997, m: 18.16605, s: 0.1331028 },
    { ageDays: 4627, l: -2.371163, m: 18.22213, s: 0.1333227 },
    { ageDays: 4657, l: -2.361779, m: 18.27663, s: 0.1335233 },
    { ageDays: 4687, l: -2.352522, m: 18.33136, s: 0.1337122 },
    { ageDays: 4718, l: -2.343082, m: 18.38813, s: 0.1338951 },
    { ageDays: 4748, l: -2.334064, m: 18.44328, s: 0.1340606 },
    { ageDays: 4779, l: -2.32486, m: 18.50048, s: 0.1342197 },
    { ageDays: 4809, l: -2.316059, m: 18.55602, s: 0.1343625 },
    { ageDays: 4840, l: -2.307069, m: 18.6136, s: 0.1344985 },
    { ageDays: 4870, l: -2.298463, m: 18.6695, s: 0.1346194 },
    { ageDays: 4900, l: -2.289945, m: 18.72557, s: 0.1347298 },
    { ageDays: 4931, l: -2.28123, m: 18.78367, s: 0.1348332 },
    { ageDays: 4961, l: -2.272875, m: 18.84005, s: 0.134923 },
    { ageDays: 4992, l: -2.264318, m: 18.89845, s: 0.1350055 },
    { ageDays: 5022, l: -2.256105, m: 18.9551, s: 0.1350757 },
    { ageDays: 5053, l: -2.247684, m: 19.01377, s: 0.1351383 },
    { ageDays: 5083, l: -2.239593, m: 19.07067, s: 0.1351897 },
    { ageDays: 5114, l: -2.231287, m: 19.12958, s: 0.1352335 },
    { ageDays: 5144, l: -2.223299, m: 19.18669, s: 0.1352672 },
    { ageDays: 5174, l: -2.215354, m: 19.24389, s: 0.1352924 },
    { ageDays: 5205, l: -2.207186, m: 19.30309, s: 0.13531 },
    { ageDays: 5235, l: -2.199317, m: 19.36046, s: 0.1353191 },
    { ageDays: 5266, l: -2.191219, m: 19.4198, s: 0.1353205 },
    { ageDays: 5296, l: -2.18341, m: 19.4773, s: 0.1353145 },
    { ageDays: 5327, l: -2.175366, m: 19.53677, s: 0.1353008 },
    { ageDays: 5357, l: -2.167603, m: 19.59436, s: 0.1352806 },
    { ageDays: 5387, l: -2.159858, m: 19.65199, s: 0.135254 },
    { ageDays: 5418, l: -2.151869, m: 19.71158, s: 0.1352199 },
    { ageDays: 5448, l: -2.144152, m: 19.76926, s: 0.1351809 },
    { ageDays: 5479, l: -2.136187, m: 19.82887, s: 0.1351346 },
    { ageDays: 5509, l: -2.128488, m: 19.88657, s: 0.1350843 },
    { ageDays: 5540, l: -2.12054, m: 19.94618, s: 0.1350271 },
    { ageDays: 5570, l: -2.112853, m: 20.00386, s: 0.1349668 },
    { ageDays: 5601, l: -2.104914, m: 20.06344, s: 0.1348997 },
    { ageDays: 5631, l: -2.097236, m: 20.12107, s: 0.1348306 },
    { ageDays: 5661, l: -2.089562, m: 20.17866, s: 0.1347575 },
    { ageDays: 5692, l: -2.081636, m: 20.23813, s: 0.1346782 },
    { ageDays: 5722, l: -2.073971, m: 20.29562, s: 0.1345981 },
    { ageDays: 5753, l: -2.066056, m: 20.35496, s: 0.1345123 },
    { ageDays: 5783, l: -2.058405, m: 20.41231, s: 0.1344264 },
    { ageDays: 5814, l: -2.050509, m: 20.47149, s: 0.1343352 },
    { ageDays: 5844, l: -2.04288, m: 20.52868, s: 0.1342448 },
    { ageDays: 5874, l: -2.035266, m: 20.58576, s: 0.1341528 },
    { ageDays: 5905, l: -2.027419, m: 20.64464, s: 0.1340561 },
    { ageDays: 5935, l: -2.019849, m: 20.7015, s: 0.1339614 },
    { ageDays: 5966, l: -2.012057, m: 20.76013, s: 0.1338628 },
    { ageDays: 5996, l: -2.004548, m: 20.81673, s: 0.1337669 },
    { ageDays: 6027, l: -1.996829, m: 20.87507, s: 0.1336676 },
    { ageDays: 6057, l: -1.989406, m: 20.93137, s: 0.1335718 },
    { ageDays: 6088, l: -1.981789, m: 20.98938, s: 0.1334734 },
    { ageDays: 6118, l: -1.974479, m: 21.04535, s: 0.1333791 },
    { ageDays: 6148, l: -1.967235, m: 21.10114, s: 0.133286 },
    { ageDays: 6179, l: -1.95983, m: 21.1586, s: 0.1331915 },
    { ageDays: 6209, l: -1.952749, m: 21.214, s: 0.133102 },
    { ageDays: 6240, l: -1.945531, m: 21.27102, s: 0.1330119 },
    { ageDays: 6270, l: -1.938652, m: 21.32599, s: 0.1329274 },
    { ageDays: 6301, l: -1.931663, m: 21.38255, s: 0.1328432 },
    { ageDays: 6331, l: -1.925027, m: 21.43706, s: 0.1327651 },
    { ageDays: 6361, l: -1.918527, m: 21.49131, s: 0.1326908 },
    { ageDays: 6392, l: -1.911965, m: 21.54711, s: 0.1326184 },
    { ageDays: 6422, l: -1.905777, m: 21.60083, s: 0.1325528 },
    { ageDays: 6453, l: -1.899562, m: 21.65606, s: 0.1324901 },
    { ageDays: 6483, l: -1.893734, m: 21.70922, s: 0.1324348 },
    { ageDays: 6514, l: -1.887917, m: 21.76384, s: 0.1323836 },
    { ageDays: 6544, l: -1.882499, m: 21.81639, s: 0.1323401 },
    { ageDays: 6575, l: -1.877132, m: 21.87036, s: 0.132302 },
    { ageDays: 6605, l: -1.872173, m: 21.92227, s: 0.132272 },
    { ageDays: 6635, l: -1.867461, m: 21.97384, s: 0.1322493 },
    { ageDays: 6666, l: -1.862863, m: 22.02676, s: 0.1322338 },
    { ageDays: 6696, l: -1.858682, m: 22.07763, s: 0.132227 },
    { ageDays: 6727, l: -1.854654, m: 22.12981, s: 0.1322289 },
    { ageDays: 6757, l: -1.851051, m: 22.17993, s: 0.1322398 },
    { ageDays: 6788, l: -1.847642, m: 22.23131, s: 0.1322609 },
    { ageDays: 6818, l: -1.844656, m: 22.28065, s: 0.1322913 },
    { ageDays: 6848, l: -1.841986, m: 22.32959, s: 0.1323319 },
    { ageDays: 6879, l: -1.839566, m: 22.37973, s: 0.132385 },
    { ageDays: 6909, l: -1.837562, m: 22.42784, s: 0.1324477 },
    { ageDays: 6940, l: -1.835841, m: 22.47709, s: 0.1325246 },
    { ageDays: 6970, l: -1.834518, m: 22.52433, s: 0.1326113 },
    { ageDays: 7001, l: -1.833511, m: 22.57267, s: 0.1327141 },
    { ageDays: 7031, l: -1.832884, m: 22.619, s: 0.1328267 },
    { ageDays: 7062, l: -1.832594, m: 22.6664, s: 0.1329572 },
    { ageDays: 7092, l: -1.83266, m: 22.7118, s: 0.1330977 },
    { ageDays: 7122, l: -1.833061, m: 22.75673, s: 0.1332526 },
    { ageDays: 7153, l: -1.833824, m: 22.80266, s: 0.1334284 },
    { ageDays: 7183, l: -1.834887, m: 22.84661, s: 0.1336142 },
    { ageDays: 7214, l: -1.836315, m: 22.89152, s: 0.1338229 },
    { ageDays: 7244, l: -1.838008, m: 22.93448, s: 0.1340417 },
    { ageDays: 7275, l: -1.840062, m: 22.97835, s: 0.1342855 },
    { ageDays: 7305, l: -1.84233, m: 23.02029, s: 0.1345394 },
  ],
  female: [
    { ageDays: 731, l: -0.9878533, m: 16.42224, s: 0.08543779 },
    { ageDays: 761, l: -1.063362, m: 16.35309, s: 0.08461162 },
    { ageDays: 791, l: -1.141962, m: 16.28608, s: 0.08383738 },
    { ageDays: 822, l: -1.226199, m: 16.21905, s: 0.08309089 },
    { ageDays: 852, l: -1.310397, m: 16.1563, s: 0.08241954 },
    { ageDays: 883, l: -1.399873, m: 16.09364, s: 0.08177775 },
    { ageDays: 913, l: -1.488527, m: 16.03509, s: 0.08120619 },
    { ageDays: 944, l: -1.581897, m: 15.97673, s: 0.08066596 },
    { ageDays: 974, l: -1.673568, m: 15.92231, s: 0.08019119 },
    { ageDays: 1004, l: -1.766123, m: 15.86991, s: 0.07976296 },
    { ageDays: 1035, l: -1.862243, m: 15.81786, s: 0.07936864 },
    { ageDays: 1065, l: -1.955275, m: 15.76951, s: 0.07903306 },
    { ageDays: 1096, l: -2.050946, m: 15.72162, s: 0.07873318 },
    { ageDays: 1126, l: -2.142623, m: 15.67726, s: 0.07848778 },
    { ageDays: 1157, l: -2.235948, m: 15.63346, s: 0.07827991 },
    { ageDays: 1187, l: -2.324461, m: 15.59303, s: 0.07812248 },
    { ageDays: 1218, l: -2.413625, m: 15.55326, s: 0.07800446 },
    { ageDays: 1248, l: -2.497295, m: 15.51669, s: 0.07793298 },
    { ageDays: 1278, l: -2.578031, m: 15.482, s: 0.07790303 },
    { ageDays: 1309, l: -2.658039, m: 15.4481, s: 0.07791522 },
    { ageDays: 1339, l: -2.731864, m: 15.41717, s: 0.07796827 },
    { ageDays: 1370, l: -2.804168, m: 15.38712, s: 0.07806516 },
    { ageDays: 1400, l: -2.870071, m: 15.35986, s: 0.07819913 },
    { ageDays: 1431, l: -2.933798, m: 15.33356, s: 0.07837848 },
    { ageDays: 1461, l: -2.991113, m: 15.30989, s: 0.07859103 },
    { ageDays: 1491, l: -3.044057, m: 15.28796, s: 0.07884132 },
    { ageDays: 1522, l: -3.094127, m: 15.26708, s: 0.07913887 },
    { ageDays: 1552, l: -3.138083, m: 15.24859, s: 0.07946377 },
    { ageDays: 1583, l: -3.178879, m: 15.23123, s: 0.07983688 },
    { ageDays: 1613, l: -3.213937, m: 15.21609, s: 0.08023331 },
    { ageDays: 1644, l: -3.245681, m: 15.20214, s: 0.08067862 },
    { ageDays: 1674, l: -3.272169, m: 15.19025, s: 0.08114316 },
    { ageDays: 1705, l: -3.295298, m: 15.17961, s: 0.08165696 },
    { ageDays: 1735, l: -3.313721, m: 15.17087, s: 0.08218589 },
    { ageDays: 1765, l: -3.328405, m: 15.16364, s: 0.08274504 },
    { ageDays: 1796, l: -3.339826, m: 15.15773, s: 0.08335353 },
    { ageDays: 1826, l: -3.347426, m: 15.1535, s: 0.08397105 },
    { ageDays: 1857, l: -3.351904, m: 15.15064, s: 0.08463768 },
    { ageDays: 1887, l: -3.353159, m: 15.14931, s: 0.0853093 },
    { ageDays: 1918, l: -3.351475, m: 15.1494, s: 0.08602955 },
    { ageDays: 1948, l: -3.347149, m: 15.15087, s: 0.08675088 },
    { ageDays: 1978, l: -3.340354, m: 15.15368, s: 0.08749501 },
    { ageDays: 2009, l: -3.330934, m: 15.15797, s: 0.08828675 },
    { ageDays: 2039, l: -3.319677, m: 15.16344, s: 0.08907387 },
    { ageDays: 2070, l: -3.306015, m: 15.17044, s: 0.08990771 },
    { ageDays: 2100, l: -3.290999, m: 15.17848, s: 0.09073334 },
    { ageDays: 2131, l: -3.273798, m: 15.18808, s: 0.09160464 },
    { ageDays: 2161, l: -3.255678, m: 15.1986, s: 0.09246429 },
    { ageDays: 2192, l: -3.235587, m: 15.21071, s: 0.09336844 },
    { ageDays: 2222, l: -3.214965, m: 15.22362, s: 0.09425767 },
    { ageDays: 2252, l: -3.193312, m: 15.23768, s: 0.09515987 },
    { ageDays: 2283, l: -3.169985, m: 15.25338, s: 0.09610464 },
    { ageDays: 2313, l: -3.146608, m: 15.2697, s: 0.09703 },
    { ageDays: 2344, l: -3.121741, m: 15.2877, s: 0.09799653 },
    { ageDays: 2374, l: -3.09709, m: 15.3062, s: 0.09894087 },
    { ageDays: 2405, l: -3.071115, m: 15.32641, s: 0.09992492 },
    { ageDays: 2435, l: -3.045581, m: 15.34702, s: 0.1008842 },
    { ageDays: 2465, l: -3.01974, m: 15.36862, s: 0.1018494 },
    { ageDays: 2496, l: -2.992795, m: 15.39199, s: 0.102852 },
    { ageDays: 2526, l: -2.966556, m: 15.41558, s: 0.1038264 },
    { ageDays: 2557, l: -2.939344, m: 15.44096, s: 0.1048366 },
    { ageDays: 2587, l: -2.912973, m: 15.46646, s: 0.1058165 },
    { ageDays: 2618, l: -2.885745, m: 15.49377, s: 0.1068306 },
    { ageDays: 2648, l: -2.859467, m: 15.52111, s: 0.1078126 },
    { ageDays: 2679, l: -2.832436, m: 15.55027, s: 0.108827 },
    { ageDays: 2709, l: -2.806437, m: 15.57937, s: 0.1098077 },
    { ageDays: 2739, l: -2.780634, m: 15.60931, s: 0.1107867 },
    { ageDays: 2770, l: -2.754212, m: 15.64111, s: 0.1117955 },
    { ageDays: 2800, l: -2.728908, m: 15.67271, s: 0.1127686 },
    { ageDays: 2831, l: -2.703063, m: 15.70618, s: 0.1137699 },
    { ageDays: 2861, l: -2.678371, m: 15.73935, s: 0.1147341 },
    { ageDays: 2892, l: -2.653208, m: 15.77443, s: 0.115725 },
    { ageDays: 2922, l: -2.62922, m: 15.80912, s: 0.1166778 },
    { ageDays: 2952, l: -2.605603, m: 15.84453, s: 0.117624 },
    { ageDays: 2983, l: -2.581606, m: 15.88186, s: 0.1185943 },
    { ageDays: 3013, l: -2.558791, m: 15.91869, s: 0.1195255 },
    { ageDays: 3044, l: -2.535649, m: 15.95744, s: 0.1204789 },
    { ageDays: 3074, l: -2.513683, m: 15.99561, s: 0.1213927 },
    { ageDays: 3105, l: -2.491438, m: 16.03573, s: 0.1223271 },
    { ageDays: 3135, l: -2.470355, m: 16.07518, s: 0.1232214 },
    { ageDays: 3166, l: -2.449035, m: 16.11658, s: 0.1241348 },
    { ageDays: 3196, l: -2.42886, m: 16.15725, s: 0.1250078 },
    { ageDays: 3226, l: -2.409136, m: 16.19848, s: 0.1258697 },
    { ageDays: 3257, l: -2.389233, m: 16.24169, s: 0.1267481 },
    { ageDays: 3287, l: -2.370434, m: 16.28404, s: 0.1275862 },
    { ageDays: 3318, l: -2.351492, m: 16.32837, s: 0.1284392 },
    { ageDays: 3348, l: -2.333625, m: 16.37178, s: 0.1292519 },
    { ageDays: 3379, l: -2.315643, m: 16.41717, s: 0.1300782 },
    { ageDays: 3409, l: -2.298706, m: 16.46157, s: 0.1308642 },
    { ageDays: 3439, l: -2.282225, m: 16.50645, s: 0.1316368 },
    { ageDays: 3470, l: -2.265669, m: 16.55329, s: 0.1324206 },
    { ageDays: 3500, l: -2.250107, m: 16.59906, s: 0.1331648 },
    { ageDays: 3531, l: -2.234499, m: 16.64681, s: 0.1339189 },
    { ageDays: 3561, l: -2.219847, m: 16.69342, s: 0.1346338 },
    { ageDays: 3592, l: -2.205173, m: 16.742, s: 0.1353572 },
    { ageDays: 3622, l: -2.191419, m: 16.7894, s: 0.1360422 },
    { ageDays: 3653, l: -2.177665, m: 16.83875, s: 0.1367341 },
    { ageDays: 3683, l: -2.164794, m: 16.88687, s: 0.1373883 },
    { ageDays: 3713, l: -2.152351, m: 16.93531, s: 0.1380272 },
    { ageDays: 3744, l: -2.13994, m: 16.98571, s: 0.1386712 },
    { ageDays: 3774, l: -2.128357, m: 17.03479, s: 0.1392787 },
    { ageDays: 3805, l: -2.116825, m: 17.08581, s: 0.13989 },
    { ageDays: 3835, l: -2.106085, m: 17.13547, s: 0.1404656 },
    { ageDays: 3866, l: -2.095414, m: 17.18705, s: 0.1410439 },
    { ageDays: 3896, l: -2.085499, m: 17.23723, s: 0.1415875 },
    { ageDays: 3926, l: -2.075982, m: 17.28763, s: 0.1421153 },
    { ageDays: 3957, l: -2.066563, m: 17.33995, s: 0.142644 },
    { ageDays: 3987, l: -2.057845, m: 17.39079, s: 0.1431396 },
    { ageDays: 4018, l: -2.049243, m: 17.44353, s: 0.1436351 },
    { ageDays: 4048, l: -2.041306, m: 17.49475, s: 0.1440986 },
    { ageDays: 4079, l: -2.033502, m: 17.54785, s: 0.1445609 },
    { ageDays: 4109, l: -2.026329, m: 17.59939, s: 0.1449924 },
    { ageDays: 4140, l: -2.019305, m: 17.6528, s: 0.1454218 },
    { ageDays: 4170, l: -2.012879, m: 17.70461, s: 0.1458215 },
    { ageDays: 4200, l: -2.006813, m: 17.75653, s: 0.1462056 },
    { ageDays: 4231, l: -2.000921, m: 17.81028, s: 0.1465864 },
    { ageDays: 4261, l: -1.995577, m: 17.86239, s: 0.1469393 },
    { ageDays: 4292, l: -1.990421, m: 17.91632, s: 0.147288 },
    { ageDays: 4322, l: -1.985782, m: 17.96857, s: 0.1476101 },
    { ageDays: 4353, l: -1.981347, m: 18.0226, s: 0.1479273 },
    { ageDays: 4383, l: -1.977397, m: 18.07493, s: 0.1482191 },
    { ageDays: 4413, l: -1.973781, m: 18.12728, s: 0.1484962 },
    { ageDays: 4444, l: -1.97039, m: 18.18139, s: 0.1487673 },
    { ageDays: 4474, l: -1.967439, m: 18.23375, s: 0.1490151 },
    { ageDays: 4505, l: -1.964728, m: 18.28784, s: 0.1492562 },
    { ageDays: 4535, l: -1.962428, m: 18.34016, s: 0.1494753 },
    { ageDays: 4566, l: -1.960382, m: 18.39418, s: 0.1496872 },
    { ageDays: 4596, l: -1.958718, m: 18.4464, s: 0.1498784 },
    { ageDays: 4627, l: -1.957321, m: 18.5003, s: 0.1500619 },
    { ageDays: 4657, l: -1.956279, m: 18.55238, s: 0.150226 },
    { ageDays: 4687, l: -1.955537, m: 18.60437, s: 0.1503771 },
    { ageDays: 4718, l: -1.955081, m: 18.658, s: 0.1505199 },
    { ageDays: 4748, l: -1.954938, m: 18.70978, s: 0.1506453 },
    { ageDays: 4779, l: -1.955095, m: 18.76316, s: 0.150762 },
    { ageDays: 4809, l: -1.955537, m: 18.81468, s: 0.1508626 },
    { ageDays: 4840, l: -1.956291, m: 18.86777, s: 0.1509543 },
    { ageDays: 4870, l: -1.957304, m: 18.91898, s: 0.1510312 },
    { ageDays: 4900, l: -1.958592, m: 18.97003, s: 0.1510968 },
    { ageDays: 4931, l: -1.960209, m: 19.02259, s: 0.1511531 },
    { ageDays: 4961, l: -1.962046, m: 19.07326, s: 0.1511967 },
    { ageDays: 4992, l: -1.964221, m: 19.12542, s: 0.1512307 },
    { ageDays: 5022, l: -1.966591, m: 19.17567, s: 0.1512533 },
    { ageDays: 5053, l: -1.969309, m: 19.22737, s: 0.1512663 },
    { ageDays: 5083, l: -1.972197, m: 19.27716, s: 0.1512691 },
    { ageDays: 5114, l: -1.975442, m: 19.32836, s: 0.1512623 },
    { ageDays: 5144, l: -1.97883, m: 19.37765, s: 0.1512466 },
    { ageDays: 5174, l: -1.982458, m: 19.42667, s: 0.1512223 },
    { ageDays: 5205, l: -1.986456, m: 19.47704, s: 0.1511884 },
    { ageDays: 5235, l: -1.990559, m: 19.5255, s: 0.1511475 },
    { ageDays: 5266, l: -1.995038, m: 19.57526, s: 0.1510972 },
    { ageDays: 5296, l: -1.999598, m: 19.62311, s: 0.151041 },
    { ageDays: 5327, l: -2.004538, m: 19.67222, s: 0.1509757 },
    { ageDays: 5357, l: -2.009534, m: 19.71943, s: 0.1509057 },
    { ageDays: 5387, l: -2.014737, m: 19.7663, s: 0.1508294 },
    { ageDays: 5418, l: -2.020326, m: 19.81437, s: 0.1507444 },
    { ageDays: 5448, l: -2.025934, m: 19.86053, s: 0.1506564 },
    { ageDays: 5479, l: -2.031928, m: 19.90786, s: 0.1505601 },
    { ageDays: 5509, l: -2.037916, m: 19.95328, s: 0.1504619 },
    { ageDays: 5540, l: -2.044291, m: 19.99981, s: 0.1503558 },
    { ageDays: 5570, l: -2.050634, m: 20.04446, s: 0.150249 },
    { ageDays: 5601, l: -2.057363, m: 20.09017, s: 0.1501348 },
    { ageDays: 5631, l: -2.064034, m: 20.134, s: 0.1500209 },
    { ageDays: 5661, l: -2.070856, m: 20.17741, s: 0.1499042 },
    { ageDays: 5692, l: -2.078056, m: 20.22182, s: 0.149781 },
    { ageDays: 5722, l: -2.085162, m: 20.26437, s: 0.1496597 },
    { ageDays: 5753, l: -2.092638, m: 20.30787, s: 0.1495326 },
    { ageDays: 5783, l: -2.099994, m: 20.34952, s: 0.1494083 },
    { ageDays: 5814, l: -2.10771, m: 20.39207, s: 0.1492791 },
    { ageDays: 5844, l: -2.115281, m: 20.43278, s: 0.1491537 },
    { ageDays: 5874, l: -2.122943, m: 20.47302, s: 0.1490284 },
    { ageDays: 5905, l: -2.130947, m: 20.5141, s: 0.1488994 },
    { ageDays: 5935, l: -2.138767, m: 20.55336, s: 0.1487756 },
    { ageDays: 5966, l: -2.146912, m: 20.59341, s: 0.1486491 },
    { ageDays: 5996, l: -2.154846, m: 20.63166, s: 0.1485287 },
    { ageDays: 6027, l: -2.163087, m: 20.67065, s: 0.1484067 },
    { ageDays: 6057, l: -2.171092, m: 20.70785, s: 0.1482915 },
    { ageDays: 6088, l: -2.17938, m: 20.74575, s: 0.148176 },
    { ageDays: 6118, l: -2.187405, m: 20.78189, s: 0.1480682 },
    { ageDays: 6148, l: -2.195423, m: 20.81749, s: 0.1479646 },
    { ageDays: 6179, l: -2.203687, m: 20.85371, s: 0.1478625 },
    { ageDays: 6209, l: -2.211649, m: 20.88819, s: 0.1477692 },
    { ageDays: 6240, l: -2.219829, m: 20.92324, s: 0.1476789 },
    { ageDays: 6270, l: -2.227681, m: 20.95659, s: 0.1475979 },
    { ageDays: 6301, l: -2.235714, m: 20.99045, s: 0.1475214 },
    { ageDays: 6331, l: -2.243398, m: 21.02264, s: 0.147455 },
    { ageDays: 6361, l: -2.250977, m: 21.05423, s: 0.1473964 },
    { ageDays: 6392, l: -2.258682, m: 21.08627, s: 0.1473448 },
    { ageDays: 6422, l: -2.266001, m: 21.11666, s: 0.1473041 },
    { ageDays: 6453, l: -2.273405, m: 21.14743, s: 0.147272 },
    { ageDays: 6483, l: -2.280402, m: 21.1766, s: 0.1472513 },
    { ageDays: 6514, l: -2.287438, m: 21.20609, s: 0.1472412 },
    { ageDays: 6544, l: -2.294042, m: 21.234, s: 0.147243 },
    { ageDays: 6575, l: -2.300641, m: 21.26218, s: 0.1472573 },
    { ageDays: 6605, l: -2.306788, m: 21.28881, s: 0.1472839 },
    { ageDays: 6635, l: -2.312688, m: 21.3148, s: 0.1473236 },
    { ageDays: 6666, l: -2.318506, m: 21.34098, s: 0.1473789 },
    { ageDays: 6696, l: -2.323845, m: 21.36566, s: 0.147447 },
    { ageDays: 6727, l: -2.329047, m: 21.39047, s: 0.1475332 },
    { ageDays: 6757, l: -2.333757, m: 21.4138, s: 0.1476323 },
    { ageDays: 6788, l: -2.338274, m: 21.43722, s: 0.1477518 },
    { ageDays: 6818, l: -2.342286, m: 21.45919, s: 0.1478847 },
    { ageDays: 6848, l: -2.34593, m: 21.48049, s: 0.148035 },
    { ageDays: 6879, l: -2.349291, m: 21.50178, s: 0.1482094 },
    { ageDays: 6909, l: -2.352135, m: 21.52168, s: 0.1483974 },
    { ageDays: 6940, l: -2.354636, m: 21.54151, s: 0.1486122 },
    { ageDays: 6970, l: -2.356614, m: 21.55999, s: 0.1488405 },
    { ageDays: 7001, l: -2.358186, m: 21.57835, s: 0.1490985 },
    { ageDays: 7031, l: -2.359235, m: 21.59538, s: 0.1493701 },
    { ageDays: 7062, l: -2.359814, m: 21.61223, s: 0.1496742 },
    { ageDays: 7092, l: -2.359873, m: 21.6278, s: 0.149992 },
    { ageDays: 7122, l: -2.359421, m: 21.64264, s: 0.1503335 },
    { ageDays: 7153, l: -2.358408, m: 21.6572, s: 0.1507122 },
    { ageDays: 7183, l: -2.356889, m: 21.67053, s: 0.1511043 },
    { ageDays: 7214, l: -2.354744, m: 21.68353, s: 0.1515367 },
    { ageDays: 7244, l: -2.352101, m: 21.69534, s: 0.1519825 },
    { ageDays: 7275, l: -2.348768, m: 21.70674, s: 0.1524721 },
    { ageDays: 7305, l: -2.344959, m: 21.717, s: 0.1529747 },
  ],
};
